// MentorPage.js — Mentor 2.0: LMS Híbrido con video embebido + chat IA
import BloqueoSuscripcion from '../components/suscripcion/BloqueoSuscripcion';
import { crearApiScorm12 } from '../utils/scormApi';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import VisorPdf from '../components/mentor/VisorPdf';
import VisorLectura from '../components/mentor/VisorLectura';

import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { mentor2Service, realtimeSessionService, BACKEND_BASE, API_BASE_URL } from '../services/api';
import { RealtimeSession, SESSION_STATES, isRealtimeSupported } from '../services/realtimeService';
import { useAuth } from '../contexts/AuthContext';
import { useIdioma } from '../contexts/IdiomaContext';
import { conI18n } from '../utils/i18nTexto';
import { useProject } from '../contexts/ProjectContext';
import { buscarCodigoPais } from '../utils/countryCodes';
import TutorPanel, { TutorLlamador } from '../components/mentor/TutorPanel';
import MiEntrega from '../components/entregas/MiEntrega';

// Contenido que se abre dentro del reproductor sin Vimeo: interactivo, YouTube,
// PDF o lectura (texto, sin archivo). El alumno lo marca como completado.
const esEmbebido = (v) => !!v && (
  (v.tipo === 'lectura' && !!v.texto_lectura) ||
  (!!v.file_path && ['interactivo', 'video', 'pdf'].includes(v.tipo))
);

// ─── Media Session: ancla de audio silencioso ───
// El video vive en un iframe cross-origin de Vimeo, así que el navegador
// atribuye la "media session" (controles de SO / pantalla de bloqueo / teclas
// multimedia) al iframe, NO a nuestra página. Para que los controles del SO
// invoquen NUESTROS handlers (play/pausa/±10s/siguiente-anterior lección),
// reproducimos en NUESTRA página un audio silencioso en loop sincronizado con
// el video: eso hace que la sesión de medios activa sea la nuestra.
const makeSilentAudioURL = () => {
  const sampleRate = 8000, seconds = 2, numSamples = sampleRate * seconds;
  const dataSize = numSamples; // 8-bit mono
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeStr = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
  writeStr(0, 'RIFF'); view.setUint32(4, 36 + dataSize, true); writeStr(8, 'WAVE');
  writeStr(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
  view.setUint16(22, 1, true); view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate, true); view.setUint16(32, 1, true); view.setUint16(34, 8, true);
  writeStr(36, 'data'); view.setUint32(40, dataSize, true);
  for (let i = 0; i < numSamples; i++) view.setUint8(44 + i, 128); // 128 = silencio (8-bit unsigned)
  let bin = ''; const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return 'data:audio/wav;base64,' + btoa(bin);
};
const SILENT_WAV = makeSilentAudioURL();

// Resuelve la foto del docente (path relativo → URL) y la bandera desde el país.
const resolveDocenteFoto = (img) => !img ? null : (String(img).startsWith('http') ? img : `${BACKEND_BASE}/${img}`);
const flagFromPais = (pais, w = 20) => {
  if (!pais) return null;
  const c = buscarCodigoPais(pais);
  return c?.iso ? `https://flagcdn.com/w${w}/${c.iso.toLowerCase()}.png` : null;
};

// ─── Animaciones ───
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const slideIn = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;

// Resplandor/parpadeo para invitar a avanzar al completar la lección.
const advanceGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(8,145,178,0.55), 0 4px 16px rgba(8,145,178,0.35); transform: scale(1); }
  50% { box-shadow: 0 0 0 10px rgba(8,145,178,0), 0 6px 22px rgba(8,145,178,0.6); transform: scale(1.045); }
`;

// ─── Capa superpuesta "Avanzar a la siguiente lección" (al completar) ───
const AdvanceOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: radial-gradient(ellipse at center, rgba(8,25,45,0.72), rgba(4,10,22,0.88));
  backdrop-filter: blur(2px);
  z-index: 20;
  animation: ${fadeIn} 0.35s ease;
`;

const AdvanceCard = styled.div`
  position: relative;
  background: linear-gradient(160deg, #0f2740, #0b1a2e);
  border: 1px solid rgba(8,145,178,0.5);
  border-radius: 16px;
  padding: 22px 26px 24px;
  max-width: 380px;
  width: 100%;
  text-align: center;
  color: #e2e8f0;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
`;

const AdvanceClose = styled.button`
  position: absolute;
  top: 8px;
  right: 10px;
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  &:hover { color: #e2e8f0; }
`;

const AdvanceTitle = styled.div`
  font-size: 1.15rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 6px;
`;

const AdvanceSub = styled.div`
  font-size: 0.9rem;
  color: #94a3b8;
  margin-bottom: 18px;
`;

const AdvanceGoBtn = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  background: linear-gradient(135deg, #0891B2, #0e7490);
  animation: ${advanceGlow} 1.25s ease-in-out infinite;
  &:hover { filter: brightness(1.08); }
`;

// Compartir el avance en LinkedIn: secundario, debajo de «Avanzar», para que
// el camino principal siga siendo continuar el curso.
const AdvanceShareBtn = styled.button`
  width: 100%;
  margin-top: 10px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.18);
  background: transparent;
  color: #cbd5e1;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  svg { flex-shrink: 0; color: #0A66C2; background: #fff; border-radius: 3px; transition: color 0.15s; }
  &:hover { background: #0A66C2; border-color: #0A66C2; color: #fff; }
  &:focus-visible { outline: 2px solid #38bdf8; outline-offset: 2px; }
`;

const AdvanceShareNote = styled.div`
  margin-top: 10px;
  font-size: 0.8rem;
  line-height: 1.4;
  color: #94a3b8;
`;

const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z"/>
  </svg>
);

// ─── Layout Principal ───
const PageWrapper = styled.div`
  /* ─── Theme tokens (dark default) ─── */
  --m-bg-page: #0c1220;
  --m-bg-sidebar: #111827;
  --m-bg-card: rgba(255,255,255,0.05);
  --m-bg-input: rgba(255,255,255,0.06);
  --m-bg-input-focus: rgba(255,255,255,0.08);
  --m-bg-hover: rgba(255,255,255,0.03);
  --m-bg-hover-strong: rgba(255,255,255,0.1);
  --m-bg-active: rgba(8,145,178,0.12);
  --m-text-primary: #fff;
  --m-text-secondary: rgba(255,255,255,0.7);
  --m-text-tertiary: rgba(255,255,255,0.5);
  --m-text-muted: rgba(255,255,255,0.4);
  --m-text-faint: rgba(255,255,255,0.35);
  --m-text-disabled: rgba(255,255,255,0.2);
  --m-border: rgba(255,255,255,0.06);
  --m-border-medium: rgba(255,255,255,0.08);
  --m-border-light: rgba(255,255,255,0.1);
  --m-chat-user-bg: rgba(8,145,178,0.15);
  --m-chat-user-text: #e0f2fe;
  --m-chat-ai-bg: rgba(255,255,255,0.05);
  --m-chat-ai-text: rgba(255,255,255,0.8);
  --m-chat-ai-border: transparent;
  --m-success: #34D399;
  --m-scrollbar-thumb: rgba(255,255,255,0.08);

  ${p => p.$light && css`
    --m-bg-page: #f1f5f9;
    --m-bg-sidebar: #ffffff;
    --m-bg-card: rgba(0,0,0,0.03);
    --m-bg-input: rgba(0,0,0,0.04);
    --m-bg-input-focus: rgba(0,0,0,0.06);
    --m-bg-hover: rgba(0,0,0,0.04);
    --m-bg-hover-strong: rgba(0,0,0,0.07);
    --m-bg-active: rgba(8,145,178,0.08);
    --m-text-primary: #1e293b;
    --m-text-secondary: #475569;
    --m-text-tertiary: #64748b;
    --m-text-muted: #94a3b8;
    --m-text-faint: #94a3b8;
    --m-text-disabled: #94a3b8;
    --m-border: rgba(0,0,0,0.08);
    --m-border-medium: rgba(0,0,0,0.1);
    --m-border-light: rgba(0,0,0,0.12);
    --m-chat-user-bg: rgba(8,145,178,0.1);
    --m-chat-user-text: #0c4a6e;
    --m-chat-ai-bg: #ffffff;
    --m-chat-ai-text: #334155;
    --m-chat-ai-border: rgba(0,0,0,0.08);
    --m-success: #10b981;
    --m-scrollbar-thumb: rgba(0,0,0,0.1);
  `}

  height: 100vh;
  background: var(--m-bg-page);
  display: grid;
  grid-template-columns: ${p => {
    const sidebar = p.$collapsed ? '40px' : '280px';
    return p.$right ? `1fr ${sidebar}` : `${sidebar} 1fr`;
  }};
  overflow: hidden;
  transition: background 0.3s ease, grid-template-columns 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  /* Con sitio de sobra el tutor es una tercera columna pegada al video; por
     debajo de 1280px se superpone (el panel lo resuelve con position: fixed) y
     aquí no reserva ancho, que es del video. */
  @media (min-width: 1280px) {
    grid-template-columns: ${p => {
      const sidebar = p.$collapsed ? '40px' : '280px';
      if (!p.$tutor) return p.$right ? `1fr ${sidebar}` : `${sidebar} 1fr`;
      return p.$right ? `360px 1fr ${sidebar}` : `${sidebar} 1fr 360px`;
    }};
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.aside`
  background: var(--m-bg-sidebar);
  ${p => p.$right
    ? 'border-left: 1px solid var(--m-border); border-right: none;'
    : 'border-right: 1px solid var(--m-border); border-left: none;'}
  overflow: ${p => p.$collapsed ? 'hidden' : 'visible'};
  overflow-y: ${p => p.$collapsed ? 'hidden' : 'auto'};
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: sticky;
  top: 0;
  min-width: 0;
  /* Orden en la fila: índice y tutor en los extremos, el video en medio. */
  order: ${p => p.$right ? 3 : 1};
  transition: background 0.3s ease, border-color 0.3s ease;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    width: 300px;
    z-index: 1000;
    order: 0;
    border-right: 1px solid var(--m-border);
    border-left: none;
    overflow-y: auto;
    transform: ${p => p.$open ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s ease, background 0.3s ease;
    box-shadow: ${p => p.$open ? '4px 0 24px rgba(0,0,0,0.5)' : 'none'};
  }
`;

const SidebarOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${p => p.$open ? 'block' : 'none'};
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    z-index: 999;
  }
`;

const MainArea = styled.main`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  order: 2;

  @media (max-width: 768px) {
    order: 0;
  }
`;

// ─── Sidebar Internals ───
const SidebarHeader = styled.div`
  padding: 1.25rem;
  border-bottom: 1px solid var(--m-border);
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--m-text-tertiary);
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover { color: var(--m-text-primary); }
`;

const ProgramTitle = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  color: var(--m-text-primary);
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
`;

const ProgressBar = styled.div`
  height: 4px;
  background: var(--m-border-medium);
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #0891B2, #06b6d4);
  border-radius: 2px;
  width: ${p => p.$percent}%;
  transition: width 0.5s ease;
`;

const ProgressText = styled.span`
  font-size: 0.7rem;
  color: var(--m-text-muted);
  margin-top: 4px;
  display: block;
`;

const ModuleList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
`;

const SidebarContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  opacity: ${p => p.$collapsed ? 0 : 1};
  visibility: ${p => p.$collapsed ? 'hidden' : 'visible'};
  transition: opacity 0.2s ease, visibility 0.2s ease;

  @media (max-width: 768px) {
    opacity: 1;
    visibility: visible;
  }
`;

const SidebarHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;

const SidebarAction = styled.button`
  background: var(--m-bg-input);
  border: 1px solid var(--m-border-medium);
  color: var(--m-text-tertiary);
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
  &:hover {
    color: var(--m-text-primary);
    background: var(--m-bg-hover-strong);
    border-color: var(--m-border-light);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const CollapseTab = styled.button`
  background: none;
  border: none;
  border-top: 1px solid var(--m-border);
  color: var(--m-text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  flex-shrink: 0;
  transition: color 0.2s, background 0.2s;
  &:hover {
    color: var(--m-text-primary);
    background: var(--m-bg-hover);
  }

  svg {
    transition: transform 0.3s ease;
    transform: rotate(${p => {
      // Base SVG points left (←)
      // No collapsed + left = ← (collapse) = 0deg
      // Collapsed + left = → (expand) = 180deg
      // No collapsed + right = → (collapse) = 180deg
      // Collapsed + right = ← (expand) = 0deg
      const shouldRotate = p.$collapsed !== p.$right;
      return shouldRotate ? '180deg' : '0deg';
    }});
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ModuleGroup = styled.div`
  margin-bottom: 0.25rem;
`;

const ModuleHeader = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: 0.6rem 1.25rem;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${p => p.$status === 'completed' ? 'var(--m-success)' : p.$status === 'current' ? '#06b6d4' : 'var(--m-text-muted)'};
  font-size: ${p => (p.$mayus ? '0.8rem' : '0.86rem')};
  font-weight: 600;
  /* config_json.mentor_modulos_mayusculas === false: el título tal cual se escribió. */
  text-transform: ${p => (p.$mayus ? 'uppercase' : 'none')};
  letter-spacing: ${p => (p.$mayus ? '0.5px' : '0')};
  &:hover { background: var(--m-bg-hover); }
`;

const LessonList = styled.div`
  ${p => !p.$expanded && 'display: none;'}
`;

const LessonItem = styled.button`
  width: 100%;
  background: ${p => p.$active ? 'var(--m-bg-active)' : 'none'};
  border: none;
  border-left: 3px solid ${p =>
    p.$completed ? 'var(--m-success)' :
    p.$active ? '#0891B2' :
    'transparent'};
  padding: 0.55rem 1.25rem 0.55rem 1.5rem;
  text-align: left;
  cursor: ${p => p.$locked ? 'not-allowed' : 'pointer'};
  opacity: ${p => p.$locked ? 0.35 : 1};
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${p => p.$active ? 'var(--m-text-primary)' : 'var(--m-text-secondary)'};
  font-size: 0.82rem;
  transition: all 0.15s ease;

  &:hover {
    ${p => !p.$locked && css`background: var(--m-bg-hover);`}
  }
`;

// ── Docente (speaker) en cada lección ──
const DocenteAvatar = styled.span`
  position: relative;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
`;
const DocenteFoto = styled.img`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  border: 1.5px solid rgba(255,255,255,0.15);
  background: var(--m-bg-hover);
`;
const DocenteFotoFallback = styled.span`
  width: 26px; height: 26px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.7rem; font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #0891B2, #0e7490);
  border: 1.5px solid rgba(255,255,255,0.15);
`;
const DocenteBandera = styled.img`
  position: absolute;
  bottom: -2px; right: -3px;
  width: 13px; height: 10px;
  border-radius: 2px;
  box-shadow: 0 0 0 1.5px var(--m-bg-sidebar, #0b1220);
  object-fit: cover;
`;

// ── Cuerpo de la lección (título + progreso) ──
const LessonBody = styled.span`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
`;
const LessonTitleText = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  line-height: 1.3;
  word-break: break-word;
`;
const LessonProgress = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;
const MiniBar = styled.span`
  flex: 1;
  height: 3px;
  border-radius: 999px;
  background: rgba(255,255,255,0.12);
  overflow: hidden;
  min-width: 30px;
`;
// OJO: el ancho va por style inline (NO por prop en el template) porque el
// porcentaje cambia en cada timeupdate; interpolarlo en styled-components
// inyectaría una clase CSS nueva por cada valor y satura el DOM.
const MiniBarFill = styled.span`
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #06b6d4, #22d3ee);
  border-radius: 999px;
  transition: width 0.2s linear;
`;
const MiniTime = styled.span`
  font-size: 0.68rem;
  font-variant-numeric: tabular-nums;
  color: var(--m-text-muted);
  white-space: nowrap;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${p =>
    p.$completed ? 'var(--m-success)' :
    p.$active ? '#0891B2' :
    'var(--m-text-disabled)'};
  ${p => p.$active && css`box-shadow: 0 0 8px rgba(8,145,178,0.5);`}
`;

// ── Examen en el menú (de una lección, de un módulo o del curso) ──
const ExamenItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0.6rem 1.25rem 0.6rem ${p => (p.$nivel === 'curso' ? '1.25rem' : '1.5rem')};
  border: none;
  border-left: 3px solid ${p => (p.$estado === 'aprobado' ? 'var(--m-success)' : p.$estado === 'disponible' ? '#f59e0b' : 'transparent')};
  background: ${p => (p.$estado === 'disponible' ? 'rgba(245, 158, 11, 0.08)' : 'none')};
  color: var(--m-text-secondary);
  font: inherit;
  font-size: 0.82rem;
  text-align: left;
  cursor: ${p => (p.$estado === 'bloqueado' || p.$estado === 'agotado' ? 'not-allowed' : 'pointer')};
  opacity: ${p => (p.$estado === 'bloqueado' ? 0.55 : 1)};
  transition: background 0.15s ease;
  &:hover { ${p => (p.$estado === 'disponible' || p.$estado === 'aprobado') && css`background: var(--m-bg-hover);`} }
  &:focus-visible { outline: 2px solid #0891B2; outline-offset: -2px; }
  .ico {
    flex: none; width: 26px; height: 26px; border-radius: 7px; display: grid; place-items: center;
    background: ${p => (p.$estado === 'aprobado' ? 'rgba(16, 185, 129, 0.16)' : p.$estado === 'disponible' ? 'rgba(245, 158, 11, 0.18)' : 'var(--m-bg-hover)')};
    color: ${p => (p.$estado === 'aprobado' ? 'var(--m-success)' : p.$estado === 'disponible' ? '#f59e0b' : 'var(--m-text-tertiary)')};
    svg { width: 15px; height: 15px; }
  }
  .txt { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  strong { font-weight: 600; color: var(--m-text-primary); line-height: 1.25; }
  small { font-size: 0.72rem; color: ${p => (p.$estado === 'aprobado' ? 'var(--m-success)' : 'var(--m-text-tertiary)')}; }
`;

const IcoExamen = ({ estado }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {estado === 'bloqueado'
      ? <><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>
      : <><path d="M9 4h6a1 1 0 0 1 1 1v1H8V5a1 1 0 0 1 1-1Z" /><path d="M8 5H6.5A1.5 1.5 0 0 0 5 6.5v13A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-13A1.5 1.5 0 0 0 17.5 5H16" />{estado === 'aprobado' ? <path d="m9 13.5 2 2 4-4.5" /> : <path d="M9 12h6M9 16h4" />}</>}
  </svg>
);

// ─── Video Player ───
const VideoSection = styled.div`
  padding: 0.5rem 1rem 0;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const VideoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 0.4rem;
  padding: 0;

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
  }
`;

const HamburgerBtn = styled.button`
  display: none;
  background: var(--m-bg-input);
  border: 1px solid var(--m-border-light);
  color: var(--m-text-primary);
  width: 36px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const VideoTitle = styled.h1`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--m-text-primary);
  margin: 0;
  line-height: 1.2;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Indicaciones = styled.div`
  margin-top: 0.6rem;
  padding: 0.8rem 1rem;
  border-radius: 10px;
  border-left: 3px solid #14b6cb;
  background: rgba(20, 182, 203, 0.06);
  color: #94a3b8;
  font-size: 0.9rem;
  line-height: 1.5;
  strong { display: block; margin-bottom: 0.25rem; color: #14b6cb; font-size: 0.82rem; }
  p { margin: 0 0 0.4rem; white-space: pre-line; }
  p:last-child { margin-bottom: 0; }
  @media (max-width: 768px) { border-radius: 0; margin-top: 0; }
`;

// Un paquete de Articulate baja todo su audio e imágenes (4-7 MB) antes de
// arrancar y mientras tanto el recuadro queda en blanco: se avisa que está cargando.
const CargaEmbebido = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.9rem;
  background: #0d1117;
  color: #c9d1d9;
  font-size: 0.9rem;
  z-index: 1;
  pointer-events: none;
  transition: opacity 0.35s ease;
  &::before {
    content: '';
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: 3px solid rgba(255, 255, 255, 0.15);
    border-top-color: #14b6cb;
    animation: girarCarga 0.9s linear infinite;
  }
  @keyframes girarCarga { to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) { &::before { animation-duration: 2.5s; } }
`;

const IframeConCarga = ({ src, title }) => {
  const [cargado, setCargado] = useState(false);
  return (
    <>
      <iframe
        src={src}
        title={title}
        onLoad={() => setCargado(true)}
        allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
        allowFullScreen
      />
      <CargaEmbebido aria-hidden={cargado} style={{ opacity: cargado ? 0 : 1 }} role="status">
        {!cargado && 'Cargando contenido…'}
      </CargaEmbebido>
    </>
  );
};

// Paquete interactivo con canal SCORM 1.2 (fase 1): si el paquete trae su
// versión «con plataforma» (index_lms_html5.html + imsmanifest 1.2), se abre
// esa con window.API conectado y lo que informa se guarda en mentor/scorm.php.
// Si no, se abre el archivo de siempre. El tiempo de la sesión lo informa el
// paquete; si no lo hace, se usa el tiempo que estuvo abierto.
// Avance del paquete leído de su propio menú. Entiende los menús de Storyline:
//  · Storyline 1/2: .slidelist con ítems .depthN («visited» / «selected»); se
//    toma el nivel más profundo (el 1 suele ser el título del tema).
//  · Storyline 3/360: .cs-listitem («cs-viewed» / «cs-selected»), sin contar
//    los encabezados de escena (padre .item-collapsible).
// Paquetes sin menú de diapositivas (o de otro dominio) → null.
const leerAvanceStoryline = (iframe) => {
  try {
    const doc = iframe?.contentDocument;
    if (!doc) return null;
    let items = [];
    let vista = /\b(visited|selected)\b/;
    let actual = /\bselected\b/;
    const sl = doc.querySelector('.slidelist');
    if (sl) {
      // «depth2» o «depth2_0» según la versión de Storyline.
      const todos = [...sl.querySelectorAll('li, [class*="depth"]')];
      for (const n of [3, 2, 1]) { const re = new RegExp(`\\bdepth${n}(_\\d+)?\\b`); items = todos.filter(e => re.test(e.className || '')); if (items.length) break; }
    }
    if (!items.length) {
      // Solo el menú del reproductor (.outline-list), no botones de menú dentro de la diapositiva.
      items = [...doc.querySelectorAll('.outline-list .cs-listitem')].filter(e => !/item-collapsible/.test(e.parentElement?.className || ''));
      vista = /\bcs-(viewed|selected)\b/; actual = /\bcs-selected\b/;
    }
    if (!items.length) return null;
    const vistas = items.filter(e => vista.test(e.className || '')).length;
    const sel = items.find(e => actual.test(e.className || ''));
    return { total: items.length, vistas, actual: (sel?.textContent || '').trim().slice(0, 200) };
  } catch (e) { return null; }   // otro dominio: no se puede leer
};

// Diapositiva en pantalla en paquetes sin menú legible (con la lista que da el
// servidor): Storyline 360 marca la actual con la clase «cs-<id>»; Adobe Edge
// carga cada página (pageN.html) en un iframe interno.
const diapositivaActualPorMapa = (iframe, mapa) => {
  try {
    const doc = iframe?.contentDocument;
    if (!doc || !mapa?.ids?.length) return null;
    if (mapa.tipo === '360') {
      for (const el of doc.querySelectorAll('.slide')) {
        const m = String(el.className || '').match(/\bcs-([A-Za-z0-9]{8,})\b/);
        if (m && mapa.ids.includes(m[1])) return m[1];
      }
      return null;
    }
    if (mapa.tipo === 'edge') {
      for (const f of doc.querySelectorAll('iframe')) {
        const m = String(f.getAttribute('src') || f.contentWindow?.location?.pathname || '').match(/(page\d+)\.html/);
        if (m && mapa.ids.includes(m[1])) return m[1];
      }
    }
  } catch (e) { /* otro dominio */ }
  return null;
};

const IframeScorm = ({ media, documentId, user, fallback, title, onAvance, onCompletado }) => {
  const mapaRef = useRef(null);
  const vistasMapa = useRef(new Set());
  // Fase 4: completar una sola vez por apertura, por cualquiera de los dos criterios.
  const completadoRef = useRef(false);
  const completar = useCallback(() => {
    if (completadoRef.current) return;
    completadoRef.current = true;
    onCompletado?.();
  }, [onCompletado]);
  const contRef = useRef(null);
  // Fase 2: avance por diapositivas y tiempo a la vista (cuenta solo con la
  // pestaña visible), enviado cada 30 s y al salir.
  useEffect(() => {
    let visible = 0; let ultimo = null;
    const enviar = (cierre) => {
      const iframe = contRef.current?.querySelector('iframe');
      const av = leerAvanceStoryline(iframe) || avanceMapa(iframe);
      const cambio = av && JSON.stringify(av) !== JSON.stringify(ultimo);
      if (!visible && !cambio) return;
      const datos = { media_id: media.id, document_id: documentId, visible_seg: visible, avance: av || undefined };
      visible = 0; ultimo = av;
      if (cierre) {
        fetch(`${API_BASE_URL}/mentor/scorm.php`, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` }, body: JSON.stringify(datos) }).catch(() => {});
      } else {
        mentor2Service.scormGuardar(datos).then(r => { onAvance?.(r.data?.estado); if (r.data?.completado) completar(); }).catch(() => {});
      }
    };
    // Sin menú legible: se acumulan los ids vistos y se arma el mismo avance.
    const avanceMapa = (iframe) => {
      const m = mapaRef.current; if (!m) return null;
      const id = diapositivaActualPorMapa(iframe, m);
      if (id) vistasMapa.current.add(id);
      if (!vistasMapa.current.size) return null;
      return { total: m.ids.length, vistas: vistasMapa.current.size, actual: id ? `Diapositiva ${m.ids.indexOf(id) + 1}` : '', ids: [...vistasMapa.current] };
    };
    const tic = setInterval(() => {
      if (document.visibilityState === 'visible') visible += 5;
      const iframe0 = contRef.current?.querySelector('iframe');
      let av = leerAvanceStoryline(iframe0);
      if (!av) { const am = avanceMapa(iframe0); if (am) av = { ...am, vistas: Math.max(am.vistas, 0) }; }
      if (av) onAvance?.(prev => ({ ...(prev || {}), diapositivas_total: av.total, diapositivas_vistas: Math.max(av.vistas, prev?.diapositivas_vistas || 0), diapositiva_actual: av.actual }));
      // Con ids: enviar cada cambio (el servidor suma a los vistos en otras sesiones).
      if (av?.ids && JSON.stringify(av.ids) !== JSON.stringify(window.__ultimosIds)) { window.__ultimosIds = av.ids; enviar(false); }
      // Vio todas las diapositivas: avisar ya (el servidor lo marca al recibirlo).
      if (av && av.total > 0 && av.vistas >= Math.ceil(av.total * 0.9) && !completadoRef.current) { enviar(false); completar(); }
      else if (visible >= 30) enviar(false);
    }, 5000);
    const alOcultar = () => { if (document.visibilityState === 'hidden') enviar(true); };
    document.addEventListener('visibilitychange', alOcultar);
    return () => { clearInterval(tic); document.removeEventListener('visibilitychange', alOcultar); enviar(true); };
  }, [media.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return <div ref={contRef} style={{ display: 'contents' }}><IframeScormInterno media={media} documentId={documentId} user={user} fallback={fallback} title={title} onAvance={onAvance} onCompletado={completar} onMapa={(m) => { mapaRef.current = m; }} /></div>;
};

const IframeScormInterno = ({ media, documentId, user, fallback, title, onAvance, onCompletado, onMapa }) => {
  const [src, setSrc] = useState(null);
  const apiRef = useRef(null);
  useEffect(() => {
    let vivo = true;
    const abierto = Date.now();
    mentor2Service.scormEstado(media.id)
      .then(r => {
        if (!vivo) return;
        if (r.data?.mapa) onMapa?.(r.data.mapa);
        if (r.data?.estado) {
          const e = r.data.estado;
          const nota = e.score_raw != null ? (Number(e.score_max) ? Math.round((e.score_raw / e.score_max) * 1000) / 10 : Number(e.score_raw)) : null;
          onAvance?.({ ...e, nota, resultado: nota == null ? null : e.lesson_status === 'passed' ? 'aprobado' : e.lesson_status === 'failed' ? 'reprobado' : null });
        }
        if (!r.data?.scorm) { setSrc(fallback); return; }
        const enviar = (cmi, { fin }) => {
          // Sin tiempo de sesión (o «0000:00:00», como manda Storyline): el que estuvo abierto.
          if (fin && !/[1-9]/.test(String(cmi['cmi.core.session_time'] || ''))) {
            const t = Math.round((Date.now() - abierto) / 1000);
            cmi['cmi.core.session_time'] = `${String(Math.floor(t / 3600)).padStart(4, '0')}:${String(Math.floor(t / 60) % 60).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
          }
          const datos = { media_id: media.id, document_id: documentId, cmi, fin: !!fin };
          // Al cerrar la pestaña el pedido normal se corta: keepalive lo deja salir.
          if (fin && document.visibilityState === 'hidden') {
            fetch(`${API_BASE_URL}/mentor/scorm.php`, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` }, body: JSON.stringify(datos) }).catch(() => {});
          } else {
            mentor2Service.scormGuardar(datos).then(x => {
              if (x.data?.nota != null || x.data?.resultado) onAvance?.(prev => ({ ...(prev || {}), nota: x.data.nota, resultado: x.data.resultado }));
              if (x.data?.completado) onCompletado?.();
            }).catch(() => {});
          }
        };
        const api = crearApiScorm12({
          inicial: (() => {
            // Fase 3: retomar. Con estado guardado, el paquete vuelve a su
            // diapositiva (entry=resume + suspend_data + lesson_location).
            const e = r.data.estado || {};
            const ini = { 'cmi.core.student_id': String(user?.id || ''), 'cmi.core.student_name': (user?.nombre || user?.name || '').trim() };
            if (e.lesson_status) ini['cmi.core.lesson_status'] = e.lesson_status;
            if (e.suspend_data || e.lesson_location) {
              ini['cmi.core.entry'] = 'resume';
              if (e.suspend_data) ini['cmi.suspend_data'] = e.suspend_data;
              if (e.lesson_location) ini['cmi.core.lesson_location'] = e.lesson_location;
            }
            const seg = Number(e.tiempo_reportado_seg || 0);
            ini['cmi.core.total_time'] = `${String(Math.floor(seg / 3600)).padStart(4, '0')}:${String(Math.floor(seg / 60) % 60).padStart(2, '0')}:${String(seg % 60).padStart(2, '0')}`;
            return ini;
          })(),
          alGuardar: enviar,
          // El paquete dice que terminó: completar sin esperar el próximo guardado.
          alCambiar: (k, v) => { if (k === 'cmi.core.lesson_status' && (v === 'completed' || v === 'passed')) { api.LMSCommit(); onCompletado?.(); } },
        });
        apiRef.current = api;
        window.API = api;
        setSrc(`/backend${r.data.entrada}`);
      })
      .catch(() => { if (vivo) setSrc(fallback); });
    const alOcultar = () => { if (document.visibilityState === 'hidden') apiRef.current?._cerrar(); };
    document.addEventListener('visibilitychange', alOcultar);
    return () => {
      vivo = false;
      document.removeEventListener('visibilitychange', alOcultar);
      apiRef.current?._cerrar();
      if (window.API === apiRef.current) delete window.API;
      apiRef.current = null;
    };
  }, [media.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!src) return <CargaEmbebido role="status">Cargando contenido…</CargaEmbebido>;
  return <IframeConCarga src={src} title={title} />;
};

const NotaInteractivo = styled.span`
  display: inline-flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; margin-top: 0.45rem; font-size: 0.82rem; color: #cbd5e1;
  b { color: ${p => (p.$ok ? '#6ee7a8' : '#fca5a5')}; }
  em { font-style: normal; padding: 0.1rem 0.55rem; border-radius: 999px; font-weight: 700; font-size: 0.75rem;
    background: ${p => (p.$ok ? 'rgba(34,197,94,.15)' : 'rgba(239,68,68,.15)')}; color: ${p => (p.$ok ? '#86efac' : '#fca5a5')}; }
`;

const AvanceInteractivo = styled.span`
  display: flex; align-items: center; gap: 0.6rem; margin-top: 0.45rem; font-size: 0.8rem; color: #94a3b8;
  i { flex: none; width: 140px; height: 6px; border-radius: 999px; background: rgba(148, 163, 184, 0.25); overflow: hidden; }
  b { display: block; height: 100%; border-radius: inherit; background: #14b6cb; transition: width 0.4s ease; }
`;

const InteractivoBarra = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 0.6rem;
  padding: 0.7rem 1rem;
  border-radius: 10px;
  background: rgba(20, 182, 203, 0.08);
  border: 1px solid rgba(20, 182, 203, 0.25);
  font-size: 0.9rem;
  color: #94a3b8;
  @media (max-width: 768px) { border-radius: 0; margin-top: 0; }
  /* Celular acostado: todo el alto es para el contenido; queda solo el botón. */
  @media (max-height: 500px) and (orientation: landscape) {
    margin-top: 0.3rem;
    padding: 0.3rem 0.6rem;
    justify-content: flex-end;
    & > span:first-child { display: none; }
  }
`;

const InteractivoBoton = styled.button`
  padding: 0.55rem 1.1rem;
  border: 0;
  border-radius: 8px;
  background: ${p => (p.$sec ? 'transparent' : '#14b6cb')};
  color: ${p => (p.$sec ? '#14b6cb' : '#fff')};
  box-shadow: ${p => (p.$sec ? 'inset 0 0 0 1.5px #14b6cb' : 'none')};
  text-decoration: none;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  &:hover { filter: brightness(1.08); }
  &:focus-visible { outline: 2px solid #0e7490; outline-offset: 2px; }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 200px;
  background: #000;
  border-radius: 10px;
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: 0;
    min-height: 180px;
  }

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }

  /* Contenido embebido en el celular vertical: el recuadro toma la proporción
     de lo que muestra (diapositiva de Articulate ~4:3 con su barra, YouTube
     16:9) en vez de estirarse y dejar franjas blancas arriba y abajo. */
  ${p => (p.$embebido === 'interactivo' || p.$embebido === 'video') && css`
    @media (max-width: 768px) and (orientation: portrait) {
      flex: none;
      min-height: 0;
      aspect-ratio: ${p.$embebido === 'video' ? '16 / 9' : '4 / 3'};
    }
  `}
  @media (max-height: 500px) and (orientation: landscape) {
    min-height: 0;
  }
`;

const UnmuteBanner = styled.button`
  position: absolute;
  left: 50%;
  bottom: 64px;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: rgba(8,145,178,0.92);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 999px;
  font-size: 0.88rem;
  font-weight: 500;
  cursor: pointer;
  z-index: 7;
  pointer-events: auto;
  box-shadow: 0 6px 24px rgba(0,0,0,0.35);
  backdrop-filter: blur(8px);
  animation: ${pulse} 2s ease-in-out infinite;
  transition: background 0.2s, transform 0.2s;

  &:hover {
    background: rgba(8,145,178,1);
    transform: translateX(-50%) scale(1.04);
  }

  svg { flex-shrink: 0; }
`;

const SeekWarning = styled.div`
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(239,68,68,0.9);
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  z-index: 10;
`;

const VideoLoader = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.5);
  font-size: 0.9rem;
  animation: ${pulse} 1.5s infinite;
`;

const VideoErrorBox = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 24px;
  text-align: center;
  background: rgba(0,0,0,0.55);
  z-index: 8;

  .err-icon { color: #f59e0b; }
  .err-title { color: #fff; font-size: 1rem; font-weight: 600; }
  .err-msg { color: rgba(255,255,255,0.7); font-size: 0.85rem; max-width: 420px; line-height: 1.4; }
`;

const RetryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(8,145,178,0.95);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
  &:hover { background: rgba(8,145,178,1); transform: scale(1.04); }
`;

// ─── Custom Video Controls (Modern Glassmorphism) ───
const ControlsOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  z-index: 4;
  pointer-events: none;
`;

const ControlsClickArea = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  cursor: pointer;
  pointer-events: auto;
`;

const BigPlayBtn = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(8,145,178,0.7);
  backdrop-filter: blur(12px);
  border: 2px solid rgba(255,255,255,0.2);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 6;
  pointer-events: auto;
  box-shadow: 0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    background: rgba(8,145,178,0.85);
    box-shadow: 0 8px 40px rgba(8,145,178,0.5), inset 0 1px 0 rgba(255,255,255,0.15);
  }

  svg { margin-left: 3px; }
`;

const ControlBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 12px 10px;
  padding: 6px 14px;
  background: rgba(10,15,25,0.65);
  backdrop-filter: blur(16px);
  border-radius: 14px;
  border: 1px solid rgba(255,255,255,0.06);
  pointer-events: auto;
  opacity: ${p => p.$visible ? 1 : 0};
  transform: ${p => p.$visible ? 'translateY(0)' : 'translateY(6px)'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
`;

const CtrlBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255,255,255,0.75);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  border-radius: 6px;
  transition: all 0.15s;
  &:hover {
    color: #fff;
    background: rgba(255,255,255,0.08);
  }
`;

const CtrlBtnActive = styled(CtrlBtn)`
  color: ${p => p.$active ? '#22d3ee' : 'rgba(255,255,255,0.75)'};
  position: relative;
`;

// Overlay del subtítulo (anclado encima de la control bar, no la tapa)
const SubtitleOverlay = styled.div`
  position: absolute;
  left: 50%;
  bottom: 64px;
  transform: translateX(-50%);
  max-width: 84%;
  z-index: 6;
  pointer-events: none;
  text-align: center;
  padding: 6px 14px;
  border-radius: 6px;
  background: rgba(0,0,0,0.55);
  color: #fff;
  font-size: clamp(0.85rem, 2.4vw, 1.15rem);
  line-height: 1.35;
  font-weight: 500;
  text-shadow: 0 1px 3px rgba(0,0,0,0.9);
  white-space: pre-line;

  @media (max-width: 640px) {
    bottom: 56px;
    max-width: 92%;
  }
`;

// Menú de selección de idioma de subtítulos (sobre el botón CC)
const SubtitleMenu = styled.div`
  position: absolute;
  bottom: 44px;
  right: 0;
  background: rgba(20,24,33,0.97);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  padding: 4px;
  min-width: 140px;
  z-index: 9;
  box-shadow: 0 8px 24px rgba(0,0,0,0.5);
`;

const SubtitleMenuItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  background: ${p => p.$active ? 'rgba(34,211,238,0.15)' : 'none'};
  border: none;
  color: ${p => p.$active ? '#22d3ee' : 'rgba(255,255,255,0.85)'};
  font-size: 0.85rem;
  text-align: left;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.08); }
`;

const ProgressBarWrapper = styled.div`
  flex: 1;
  height: 3px;
  background: rgba(255,255,255,0.1);
  border-radius: 3px;
  cursor: pointer;
  position: relative;
  transition: height 0.15s;

  &:hover { height: 5px; }
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #06b6d4, #22d3ee);
  border-radius: 3px;
  width: ${p => p.$percent}%;
  transition: width 0.15s linear;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: -5px;
    top: 50%;
    transform: translateY(-50%) scale(0);
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #22d3ee;
    box-shadow: 0 0 8px rgba(34,211,238,0.5);
    transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  }

  ${ProgressBarWrapper}:hover &::after {
    transform: translateY(-50%) scale(1);
  }
`;

const MaxReachedBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: rgba(255,255,255,0.06);
  border-radius: 3px;
  width: ${p => p.$percent}%;
`;

const TimeLabel = styled.span`
  font-size: 0.68rem;
  color: rgba(255,255,255,0.5);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.3px;
`;

// ─── Navigation ───
const NavBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.4rem 1rem;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 0.4rem 0.75rem;
  }
`;

const NavBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0.35rem 0.9rem;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  border: none;
  transition: all 0.2s;

  ${p => p.$primary ? css`
    background: ${p.disabled ? 'rgba(8,145,178,0.2)' : 'linear-gradient(135deg, #0891B2, #0e7490)'};
    color: ${p.disabled ? 'var(--m-text-disabled)' : '#fff'};
    &:hover:not(:disabled) {
      box-shadow: 0 4px 16px rgba(8,145,178,0.35);
    }
    ${p.$glow && !p.disabled && css`animation: ${advanceGlow} 1.25s ease-in-out infinite;`}
  ` : css`
    background: var(--m-bg-input);
    color: ${p.disabled ? 'var(--m-text-disabled)' : 'var(--m-text-secondary)'};
    border: 1px solid var(--m-border-medium);
    &:hover:not(:disabled) { background: var(--m-bg-hover-strong); }
  `}
`;

const NavStatus = styled.span`
  font-size: 0.7rem;
  color: var(--m-text-faint);
`;

const LoadingScreen = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: var(--m-bg-page);
  color: var(--m-text-tertiary);
  font-size: 1rem;
  animation: ${pulse} 1.5s infinite;
`;

const ErrorScreen = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: var(--m-bg-page);
  color: var(--m-text-secondary);
  gap: 1rem;
  padding: 2rem;
  text-align: center;
`;

const CompletedBanner = styled.div`
  background: linear-gradient(135deg, rgba(16,185,129,0.12), rgba(8,145,178,0.08));
  border: 1px solid rgba(16,185,129,0.2);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  margin: 0.5rem 1rem 0;
  text-align: center;
  flex-shrink: 0;

  h2 { color: var(--m-success); margin: 0 0 0.25rem; font-size: 1rem; }
  p { color: var(--m-text-secondary); margin: 0; font-size: 0.8rem; }
`;

// ─── Theme Toggle ───
const ThemeToggle = styled.button`
  width: 44px;
  height: 22px;
  border-radius: 11px;
  border: 1px solid var(--m-border-light);
  background: var(--m-bg-input);
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  padding: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${p => p.$light ? '23px' : '2px'};
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${p => p.$light
      ? 'linear-gradient(135deg, #f59e0b, #fbbf24)'
      : 'linear-gradient(135deg, #6366f1, #818cf8)'};
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s ease;
    box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  }
`;

const ThemeIcon = styled.span`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  line-height: 1;
  transition: opacity 0.2s;
  user-select: none;
  color: var(--m-text-secondary);
  ${p => p.$side === 'left' ? 'left: 5px;' : 'right: 5px;'}
  opacity: ${p => p.$visible ? 1 : 0};
`;

// ══════════════════════════════════════════════════════
//  SUBTÍTULOS (parser VTT/SRT → cues)
// ══════════════════════════════════════════════════════

// "00:01:02.500" o "00:01:02,500" o "01:02.500" → segundos
const parseCueTime = (s) => {
  const m = s.trim().replace(',', '.').match(/(?:(\d+):)?(\d{1,2}):(\d{1,2}(?:\.\d+)?)/);
  if (!m) return null;
  const h = parseFloat(m[1] || 0), min = parseFloat(m[2]), sec = parseFloat(m[3]);
  return h * 3600 + min * 60 + sec;
};

// Parsea VTT o SRT a [{start, end, text}]. Tolera índices numéricos (SRT),
// cabecera WEBVTT, y bloques separados por línea en blanco.
const parseSubtitles = (raw) => {
  if (!raw || typeof raw !== 'string') return [];
  const text = raw.replace(/^﻿/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const blocks = text.split(/\n\s*\n/);
  const cues = [];
  for (let block of blocks) {
    const lines = block.split('\n').filter(l => l.trim() !== '');
    if (!lines.length) continue;
    if (/^WEBVTT/i.test(lines[0])) lines.shift();
    if (lines.length && /^\d+$/.test(lines[0].trim())) lines.shift(); // índice SRT
    if (!lines.length) continue;
    const tl = lines[0].match(/(.+?)\s*-->\s*(.+)/);
    if (!tl) continue;
    const start = parseCueTime(tl[1]);
    const end = parseCueTime(tl[2]);
    if (start == null || end == null) continue;
    const body = lines.slice(1).join('\n').replace(/<[^>]+>/g, '').trim();
    if (body) cues.push({ start, end, text: body });
  }
  return cues;
};

const NOMBRE_IDIOMA = { es: 'Español', en: 'English', pt: 'Português' };

// ── Modo pruebas (QA) ──────────────────────────────────────────────────────
// Panel flotante que solo ven los usuarios que el admin marcó como "usuario de
// pruebas": permite dar por terminada la lección, el módulo, el curso o la
// evaluación sin tener que reproducir los videos.
const PruebasPanel = styled.div`
  position: fixed;
  left: 12px;
  bottom: 12px;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.35);
  max-width: 220px;
`;

const PruebasTitulo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  button {
    background: none; border: 0; padding: 0 2px; cursor: pointer; font-family: inherit;
    color: rgba(255, 255, 255, 0.7); font-size: 0.9rem; line-height: 1;
  }
  button:hover { color: #fff; }
  font-size: 0.68rem;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  font-weight: 700;
  color: #fbbf24;
`;

const PruebasFila = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const PruebasBtn = styled.button`
  background: rgba(251, 191, 36, 0.14);
  color: #fde68a;
  border: 1px solid rgba(251, 191, 36, 0.45);
  border-radius: 8px;
  padding: 5px 9px;
  font-size: 0.74rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  &:hover { background: rgba(251, 191, 36, 0.26); }
  &:disabled { opacity: 0.6; cursor: default; }
`;

// Panel oculto: queda solo esta pastilla para volver a mostrarlo.
const PruebasMostrar = styled.button`
  position: fixed;
  left: 12px;
  bottom: 12px;
  z-index: 1200;
  background: rgba(15, 23, 42, 0.85);
  color: #fbbf24;
  border: 1px solid rgba(251, 191, 36, 0.45);
  border-radius: 999px;
  padding: 5px 11px;
  font-size: 0.7rem;
  font-weight: 700;
  font-family: inherit;
  letter-spacing: 0.4px;
  cursor: pointer;
  opacity: 0.75;
  &:hover { opacity: 1; }
`;

const PruebasMsg = styled.div`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.75);
`;

// ══════════════════════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════════════════════

const MentorPage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // A dónde vuelve el botón «Contenidos»: la página desde la que se entró al
  // curso (la manda en el estado de navegación quien abre el módulo). Sin ella,
  // la de contenidos del proyecto, como siempre. Se guarda en una ref porque el
  // reproductor navega sobre sí mismo al cambiar de lección y perdería el dato.
  const volverA = useRef(location.state?.volverA || '/contenidos');
  if (location.state?.volverA) volverA.current = location.state.volverA;
  // `/mentor/:id?modulo=<id o número>` — ancla para entrar directo a un módulo
  // desde la tarjeta del catálogo, en vez de caer siempre donde iba el alumno.
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // Modo pruebas (QA): mensaje del último atajo usado.
  const [pruebasMsg, setPruebasMsg] = useState('');
  // El tester puede ocultar el panel (queda una pastilla para volver a abrirlo);
  // se recuerda en este navegador.
  const [pruebasOculto, setPruebasOculto] = useState(() => {
    try { return localStorage.getItem('mentor_pruebas_oculto') === '1'; } catch (e) { return false; }
  });
  const alternarPruebas = (ocultar) => {
    setPruebasOculto(ocultar);
    try { localStorage.setItem('mentor_pruebas_oculto', ocultar ? '1' : '0'); } catch (e) { /* sin almacenamiento */ }
  };
  // Con la marca puesta nada queda bloqueado: el tester salta de lección en
  // lección y puede avanzar sin ver el video ni responder el quiz.
  const modoPruebas = !!user?.modo_pruebas;
  // Ref para los handlers del reproductor, que se registran una sola vez.
  const modoPruebasRef = useRef(false);
  modoPruebasRef.current = modoPruebas;

  // Tema
  const [lightMode, setLightMode] = useState(() => localStorage.getItem('mentor-theme') === 'light');
  const toggleTheme = () => {
    setLightMode(prev => {
      const next = !prev;
      localStorage.setItem('mentor-theme', next ? 'light' : 'dark');
      return next;
    });
  };

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('mentor-sidebar-collapsed', String(next));
      return next;
    });
  };

  const toggleSidebarPosition = () => {
    setSidebarRight(prev => {
      const next = !prev;
      localStorage.setItem('mentor-sidebar-position', next ? 'right' : 'left');
      return next;
    });
  };

  // Estado principal
  const [mentorData, setMentorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bloqueo, setBloqueo] = useState(null);   // suscripción vencida o curso fuera del plan
  const tiempoVimeo = useRef({ pendiente: 0, ultimo: null, videoId: null });   // segundos de Vimeo reproducidos sin guardar
  const [avanceInteractivo, setAvanceInteractivo] = useState(null);   // { diapositivas_total, diapositivas_vistas, diapositiva_actual, tiempo_visible_seg }

  // Sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => {
      const guardado = localStorage.getItem('mentor-sidebar-collapsed');
      if (guardado !== null) return guardado === 'true';
      // Celular acostado: el menú lateral se comería un tercio del ancho.
      return window.matchMedia('(max-height: 500px) and (orientation: landscape)').matches;
    }
  );
  const [sidebarRight, setSidebarRight] = useState(
    () => localStorage.getItem('mentor-sidebar-position') === 'right'
  );

  // Video player
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [videoStatus, setVideoStatus] = useState('loading'); // loading | playing | paused | completed
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [showStartOverlay, setShowStartOverlay] = useState(false);
  const [showSeekWarning, setShowSeekWarning] = useState(false);
  const [loadError, setLoadError] = useState(false); // Vimeo no logró inicializar (red/adblock/CDN)
  const [playerKey, setPlayerKey] = useState(0); // State key to force iframe remount
  const playerRef = useRef(null);
  const loadTimeoutRef = useRef(null);
  const autoRetryRef = useRef(0);   // reintentos silenciosos del video actual
  const MAX_AUTO_RETRIES = 2;
  // Espejo de isPlayerReady para los listeners de Vimeo, que capturan el closure
  // del momento en que se registraron y verían el estado congelado en false.
  const playerListoRef = useRef(false);
  const maxTimeReached = useRef(0);
  const lastSavedTime = useRef(0);
  const IFRAME_ID = 'mentor-vimeo-player';

  // Subtítulos multi-idioma. Por defecto siguen el idioma de la plataforma
  // (selector del header / preferencia del usuario / default del proyecto).
  const { idioma: idiomaPlataforma, t } = useIdioma();
  // Títulos del curso traducidos: usa la versión del idioma activo si el admin
  // la cargó (columna *_i18n) y, si no, el título original.
  const tlTitulo = useCallback((obj, campo = 'titulo') => {
    const v = conI18n(obj, campo);
    if (v == null) return '';
    if (typeof v === 'string') return v;
    return v[idiomaPlataforma] || v.es || '';
  }, [idiomaPlataforma]);
  // El chat con la IA dentro del curso se puede apagar por proyecto
  // (Admin → proyecto → «Chat con la IA en el curso»).
  const { proyecto } = useProject();
  const chatHabilitado = proyecto?.config_json?.mentor_chat !== false;
  // Compartir el avance en LinkedIn al terminar una lección (Admin → proyecto).
  const compartirCfg = proyecto?.config_json?.mentor_compartir || {};
  const compartirHabilitado = compartirCfg.habilitado === true;
  // Apagados por defecto: se encienden desde el botón CC (queda recordado).
  const [subtitlesOn, setSubtitlesOn] = useState(() => localStorage.getItem('mentor-subs-on') === 'true');
  const [subtitleLang, setSubtitleLang] = useState(idiomaPlataforma || 'es');
  const [subLangManual, setSubLangManual] = useState(false); // el usuario eligió en el CC
  const [showSubMenu, setShowSubMenu] = useState(false);

  // Seguir el idioma de la plataforma mientras el usuario no elija uno manual en el CC
  useEffect(() => {
    if (!subLangManual && idiomaPlataforma) setSubtitleLang(idiomaPlataforma);
  }, [idiomaPlataforma, subLangManual]);

  const videoActual = mentorData?.video_actual;
  useEffect(() => { setAvanceInteractivo(null); }, [videoActual?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lección que se está VIENDO ahora mismo. En modo repaso (abrir una lección ya
  // completada) NO es la misma que modulo_actual/leccion_actual, que marcan hasta
  // dónde llegó el alumno en su avance real. Todo lo que describe el video en
  // pantalla —línea de tiempo, contador, material de apoyo, rótulo de navegación,
  // título en los controles del sistema— tiene que ir por aquí.
  const moduloEnPantalla = videoActual?.modulo_numero || mentorData?.modulo_actual;
  const leccionEnPantalla = videoActual?.leccion_numero || mentorData?.leccion_actual;

  // Parsea los tracks del video actual una sola vez por video: { es: cues[], en: cues[], ... }
  const subtitleTracks = useMemo(() => {
    const out = {};
    (videoActual?.subtitulos || []).forEach(s => {
      const cues = parseSubtitles(s.contenido);
      if (cues.length) out[s.idioma] = cues;
    });
    return out;
  }, [videoActual?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const idiomasDisponibles = useMemo(() => Object.keys(subtitleTracks), [subtitleTracks]);

  // Idioma efectivo: el elegido si existe, si no el primero disponible (es preferente)
  const idiomaEfectivo = useMemo(() => {
    if (idiomasDisponibles.includes(subtitleLang)) return subtitleLang;
    if (idiomasDisponibles.includes('es')) return 'es';
    return idiomasDisponibles[0] || null;
  }, [idiomasDisponibles, subtitleLang]);

  // Cue activo según currentTime
  const activeCue = useMemo(() => {
    if (!subtitlesOn || !idiomaEfectivo) return null;
    const cues = subtitleTracks[idiomaEfectivo] || [];
    return cues.find(c => currentTime >= c.start && currentTime <= c.end) || null;
  }, [subtitlesOn, idiomaEfectivo, subtitleTracks, currentTime]);

  const toggleSubtitles = () => {
    setSubtitlesOn(prev => { localStorage.setItem('mentor-subs-on', String(!prev)); return !prev; });
    setShowSubMenu(false);
  };
  const selectSubtitleLang = (lang) => {
    setSubtitleLang(lang);
    setSubLangManual(true); // override manual: dejar de seguir el idioma de la plataforma
    if (!subtitlesOn) { setSubtitlesOn(true); localStorage.setItem('mentor-subs-on', 'true'); }
    setShowSubMenu(false);
  };

  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  // ─── Panel del tutor ───
  // Abierto por defecto donde hay sitio para tres columnas; en pantallas
  // estrechas arranca cerrado para no tapar el video.
  //
  // La preferencia se guarda POR TAMAÑO de pantalla, no una sola para todo:
  // cerrar la hoja en el teléfono —donde tapa el video y es lo natural— no
  // significa que no quiera el tutor en el escritorio, donde es una columna al
  // lado que no estorba. Son dos decisiones distintas y se recuerdan aparte.
  const hayEspacioParaColumna = () => window.innerWidth >= 1280;
  const claveTutor = (ancho) => (ancho ? 'mentor-tutor-abierto' : 'mentor-tutor-abierto-estrecho');
  const preferenciaTutor = useCallback((ancho) => {
    const guardado = localStorage.getItem(claveTutor(ancho));
    return guardado !== null ? guardado === 'true' : ancho;
  }, []);

  const [tutorAbierto, setTutorAbierto] = useState(() => preferenciaTutor(hayEspacioParaColumna()));
  // `recordar` distingue lo que decide el alumno (se guarda) de lo que abre el
  // sistema por su cuenta —el quiz, la voz— que no debe cambiarle la preferencia.
  const abrirTutor = useCallback((abierto, recordar = true) => {
    setTutorAbierto(abierto);
    if (recordar) localStorage.setItem(claveTutor(hayEspacioParaColumna()), String(abierto));
  }, []);

  // Curso sin preguntas de retroalimentación: el tutor no se abre solo (se ve
  // como «preguntas» al terminar cada contenido). Sigue a mano con su botón, y
  // si el alumno ya eligió tenerlo abierto se respeta.
  const sinRetro = mentorData?.retroalimentacion === 0;
  useEffect(() => {
    if (sinRetro && localStorage.getItem(claveTutor(hayEspacioParaColumna())) === null) setTutorAbierto(false);
  }, [sinRetro]); // eslint-disable-line react-hooks/exhaustive-deps

  // Al cruzar el umbral —girar la tablet, agrandar la ventana— se aplica la
  // preferencia de ESE tamaño, no la que venía arrastrando del otro.
  useEffect(() => {
    let anchoPrevio = hayEspacioParaColumna();
    const alRedimensionar = () => {
      const ahoraAncho = hayEspacioParaColumna();
      if (ahoraAncho === anchoPrevio) return;
      anchoPrevio = ahoraAncho;
      setTutorAbierto(preferenciaTutor(ahoraAncho));
    };
    window.addEventListener('resize', alRedimensionar);
    return () => window.removeEventListener('resize', alRedimensionar);
  }, [preferenciaTutor]);
  // Preguntas ya escritas para esta lección (se generan una vez por lección en
  // el backend). Sin ellas el alumno no sabe qué puede preguntar.
  const [sugerencias, setSugerencias] = useState([]);
  // Señal de vida: al pausar o retroceder, el tutor se ofrece una sola vez.
  const [avisoTutor, setAvisoTutor] = useState(null);
  const avisoUsadoRef = useRef(false);
  // Latido del botón la primera vez que alguien entra al modo mentor.
  const [tutorDestacado, setTutorDestacado] = useState(
    () => localStorage.getItem('mentor-tutor-conocido') !== 'true'
  );

  // Quiz
  const [quizActive, setQuizActive] = useState(false);
  const [quizPregunta, setQuizPregunta] = useState(0);
  const [quizCompleto, setQuizCompleto] = useState(false);
  // Estado de la entrega de archivos por lección ({leccion_id: 'pendiente'|'aprobada'|…}).
  const [entregaEstados, setEntregaEstados] = useState({});
  // Al completar la lección por primera vez: capa superpuesta para avanzar.
  const [showAdvanceOverlay, setShowAdvanceOverlay] = useState(false);
  // Al completar la ÚLTIMA lección por primera vez: capa de felicitación.
  const [showCongratsOverlay, setShowCongratsOverlay] = useState(false);

  // Realtime Voice
  const [realtimeActive, setRealtimeActive] = useState(false);
  const [realtimeState, setRealtimeState] = useState(SESSION_STATES.DISCONNECTED);
  const [realtimePartial, setRealtimePartial] = useState('');
  const [realtimeAudioLevel, setRealtimeAudioLevel] = useState(0);
  const [realtimeError, setRealtimeError] = useState('');
  const [realtimeMicUnavailable, setRealtimeMicUnavailable] = useState(false);
  const realtimeSessionRef = useRef(null);
  const realtimeAiBuffer = useRef('');
  const realtimeTranscriptsRef = useRef([]);

  // Derivar video actual y allowSeek (videoActual ya declarado arriba con subtítulos)
  const isReplay = useRef(false);

  // Progreso visto en ESTA sesión, por video_id. `mentorData.videos` es la foto
  // del arranque y no se actualiza mientras el alumno ve la lección; sin este
  // cache, ir a una lección ya completada y volver a la actual la reanudaba en
  // el segundo cero (se perdía lo avanzado desde que se abrió la página).
  const progresoLocal = useRef({});

  // ─── LOAD DATA ───
  const loadMentorData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await mentor2Service.start(documentId);
      const data = res.data?.data || res.data;
      setMentorData(data);

      // Expandir módulo actual
      if (data.modulo_actual) {
        setExpandedModules(prev => ({ ...prev, [data.modulo_actual]: true }));
      }
    } catch (err) {
      console.error('Error cargando mentor:', err);
      const d = err.response?.data;
      if (err.response?.status === 403 && ['suscripcion_vencida', 'fuera_de_plan', 'acceso_inactivo', 'fuera_de_plan_formacion'].includes(d?.code)) { setBloqueo(d); return; }
      setError(d?.message || t('mentor_error_cargar_programa'));
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    loadMentorData();
    return () => destroyPlayer();
  }, [loadMentorData]);

  // ─── MODO PRUEBAS (QA) ───
  // Marca como visto lo que pida el tester y recarga el estado del programa.
  const completarPrueba = async (alcance) => {
    try {
      setPruebasMsg('Marcando…');
      const res = await mentor2Service.completarPrueba({
        documentId,
        alcance,
        moduloNumero: (alcance === 'leccion' || alcance === 'modulo') ? mentorData?.modulo_actual : undefined,
        leccionNumero: alcance === 'leccion' ? mentorData?.leccion_actual : undefined,
      });
      setPruebasMsg(res.data?.message || 'Listo');
      if (alcance !== 'evaluacion') await loadMentorData();
      setTimeout(() => setPruebasMsg(''), 3000);
    } catch (err) {
      setPruebasMsg(err.response?.data?.message || 'No se pudo marcar');
    }
  };

  // ─── VIMEO PLAYER ───
  // No llamar player.destroy() aquí — Vimeo SDK remueve el iframe del DOM
  // y React crashea al no encontrar el nodo durante reconciliación.
  // El iframe se limpia via cambio de playerKey (React unmount/mount).
  const destroyPlayer = useCallback(() => {
    if (playerRef.current) {
      try {
        playerRef.current.pause();
        playerRef.current.off('play');
        playerRef.current.off('pause');
        playerRef.current.off('timeupdate');
        playerRef.current.off('ended');
        playerRef.current.off('seeked');
        playerRef.current.off('texttrackchange');
      } catch (e) { /* ignore */ }
      playerRef.current = null;
    }
    if (loadTimeoutRef.current) { clearTimeout(loadTimeoutRef.current); loadTimeoutRef.current = null; }
    setIsPlayerReady(false); playerListoRef.current = false;
    setVideoStatus('loading');
    setShowStartOverlay(false);
    setLoadError(false);
    maxTimeReached.current = 0;
    lastSavedTime.current = 0;
  }, []);

  // Contenido embebido (interactivo, YouTube o PDF): no hay reproductor de Vimeo
  // que fije el estado, así que se hace aquí. Si ya estaba completado es un
  // repaso y «Siguiente» queda habilitado.
  useEffect(() => {
    if (!videoActual || videoActual.vimeo_id) return;
    isReplay.current = !!videoActual.completado;
    setQuizActive(false);
    setQuizCompleto(!!videoActual.completado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoActual?.id]);

  // ─── VIMEO PLAYER INIT ───
  // Effect 1: When video changes, reset state and bump playerKey to remount iframe
  useEffect(() => {
    if (!videoActual?.vimeo_id) return;

    // Detach old player (no destroy — React handles DOM via key change)
    if (playerRef.current) {
      try { playerRef.current.pause(); } catch (e) { /* ignore */ }
      playerRef.current = null;
    }
    setIsPlayerReady(false); playerListoRef.current = false;
    setVideoStatus('loading');
    setShowStartOverlay(false);
    setShowSeekWarning(false);
    setLoadError(false);
    maxTimeReached.current = 0;
    lastSavedTime.current = 0;
    // Resetear la duración: si se conserva la del video anterior, un guardado
    // durante la carga del nuevo calcularía mal el % y marcaría completado=1 falso
    // (ej. pasar de un intro corto a un video largo). Se re-setea al cargar metadata.
    setVideoDuration(0);

    setQuizActive(false);
    setQuizCompleto(false);
    // Replay = el video ya estaba completado. Derivarlo del propio video evita que
    // este effect (que corre después de loadLesson) pise la detección de replay y
    // vuelva a lanzar la retroalimentación al re-ver una lección ya vista.
    isReplay.current = !!videoActual?.completado;
    autoRetryRef.current = 0;   // cada video empieza con sus reintentos limpios

    // Bump key → React remounts iframe with new src
    setPlayerKey(k => k + 1);
  }, [videoActual?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2: After iframe remounts (playerKey changes), init the Vimeo player
  useEffect(() => {
    if (!videoActual?.vimeo_id || playerKey === 0) return;

    // Un embed de Vimeo falla de vez en cuando por un tropiezo puntual de su
    // CDN (el propio player pinta "Lo sentimos, tenemos un pequeño problema").
    // Antes de culpar a la red del usuario reintentamos solos remontando el
    // iframe; solo si vuelve a fallar mostramos el mensaje con "Reintentar".
    const fallaCarga = () => {
      if (loadTimeoutRef.current) { clearTimeout(loadTimeoutRef.current); loadTimeoutRef.current = null; }
      if (autoRetryRef.current < MAX_AUTO_RETRIES) {
        autoRetryRef.current += 1;
        if (playerRef.current) {
          try { playerRef.current.off('texttrackchange'); } catch (e) { /* ignore */ }
          playerRef.current = null;
        }
        setIsPlayerReady(false); playerListoRef.current = false;
        setVideoStatus('loading');
        setPlayerKey(k => k + 1);   // remonta el iframe y vuelve a intentar
        return;
      }
      setLoadError(true);
    };

    // Watchdog: si en 15s el player no quedó listo, algo falló (CDN caído,
    // firewall corporativo/hospital o adblock). Reintento o mensaje accionable,
    // nunca un "Cargando…" eterno.
    const armarWatchdog = () => {
      if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = setTimeout(() => {
        // Con la pestaña en segundo plano el navegador congela el iframe y frena
        // los timers: que no haya cargado ahí no es culpa de la red. Se vuelve a
        // esperar en vez de acusar un fallo que el alumno vería al regresar.
        if (document.hidden) { armarWatchdog(); return; }
        if (!playerRef.current) { fallaCarga(); return; }
        playerRef.current.getDuration()
          .then(() => {/* listo, no hacer nada */})
          .catch(() => fallaCarga());
      }, 15000);
    };
    armarWatchdog();

    const initPlayer = () => {
      const iframe = document.getElementById(IFRAME_ID);
      if (!iframe) return;
      // Si quedó un player de un intento anterior, lo soltamos: hay que
      // engancharse al iframe que está ahora en pantalla.
      if (playerRef.current) {
        try { playerRef.current.off('texttrackchange'); } catch (e) { /* ignore */ }
        playerRef.current = null;
      }

      playerRef.current = new window.Vimeo.Player(iframe);

      // Ojo: Vimeo usa el evento 'error' para dos cosas muy distintas — el video
      // que no se puede cargar Y cualquier método de su API que falle (el PiP
      // automático rechazado al cambiar de pestaña, un play() interrumpido al
      // volver, un setCurrentTime tardío...). Esos últimos traen `method` y no
      // significan que el video esté roto: tumbar el reproductor por ellos era
      // lo que sacaba "No pudimos cargar el video" al regresar a la pestaña.
      const ERRORES_FATALES = ['PrivacyError', 'PasswordError', 'NotFoundError', 'PlaybackError'];
      playerRef.current.on('error', (err) => {
        const fatal = ERRORES_FATALES.includes(err?.name);
        if (!fatal && (err?.method || playerListoRef.current)) {
          console.warn('Vimeo: error no fatal, se ignora', err);
          return;
        }
        fallaCarga();
      });

      playerRef.current.ready().then(() => {
        if (!playerRef.current) return;
        if (loadTimeoutRef.current) { clearTimeout(loadTimeoutRef.current); loadTimeoutRef.current = null; }
        setLoadError(false);
        setIsPlayerReady(true); playerListoRef.current = true;

        // Apagar los subtítulos nativos de Vimeo: con controls=0 el usuario no
        // puede quitarlos y salen mal posicionados. Renderizamos los nuestros.
        // Vimeo vuelve a activar la pista marcada por defecto al empezar a
        // reproducir, así que además la apagamos cada vez que se encienda sola.
        playerRef.current.disableTextTrack().catch(() => {});
        playerRef.current.on('texttrackchange', (track) => {
          if (track && track.kind) playerRef.current?.disableTextTrack().catch(() => {});
        });

        // Reanudar SOLO después de conocer la duración real. El backend puede
        // guardar timestamps en/más allá del final (centinela 9999, o valores
        // mayores que la duración); hacer setCurrentTime ahí deja a Vimeo
        // buffering el último frame para siempre ("se queda cargando").
        // Si el iframe se remontó a mitad de la lección (reintento silencioso),
        // lo visto en esta sesión es más nuevo que lo que trajo la carga inicial.
        const enSesion = progresoLocal.current[videoActual?.id];
        const savedActual = enSesion ? enSesion.timestamp_actual : (videoActual?.timestamp_actual || 0);
        const savedMaximo = Math.max(videoActual?.timestamp_maximo || 0, enSesion?.timestamp_maximo || 0);
        const completado = !!videoActual?.completado;

        playerRef.current.getDuration().then(d => {
          setVideoDuration(d);
          const dur = d > 0 ? d : 0;

          // Reiniciar en 0 SOLO si la posición guardada cae en/más allá del final
          // (dato corrupto: centinela 9999 o valores > duración → Vimeo se cuelga).
          // Si está dentro del video, respetar la posición aunque el video esté
          // completado (el usuario puede estar re-viéndolo y dejarlo a la mitad).
          const finalCorrupto = dur > 0 && savedActual >= dur - 5;
          const resumeFrom = finalCorrupto
            ? 0
            : Math.min(savedActual, dur > 0 ? dur - 5 : savedActual);

          // maxTimeReached: si completado, permitir navegar todo el video; si no,
          // clamp del máximo alcanzado a la duración real.
          maxTimeReached.current = completado
            ? (dur || savedMaximo)
            : Math.min(savedMaximo || resumeFrom || 0, dur > 0 ? dur : (savedMaximo || resumeFrom || 0));

          if (resumeFrom > 0) {
            playerRef.current.setCurrentTime(resumeFrom).catch(() => {});
          }
        }).catch(() => {
          // Sin duración no arriesgamos un seek al final: arrancamos desde 0.
          maxTimeReached.current = savedMaximo || 0;
        });

        // Try autoplay
        playerRef.current.play().then(() => {
          return Promise.all([
            playerRef.current.getMuted(),
            playerRef.current.getVolume()
          ]);
        }).then(([isMuted, volume]) => {
          // iOS Safari fuerza autoplay muted. Dejamos correr el video y mostramos
          // banner discreto para que el alumno active el audio sin perder lección.
          if (isMuted || volume < 0.1) {
            setShowStartOverlay(true);
          }
          setVideoStatus('playing');
        }).catch(() => {
          // Autoplay completamente bloqueado — banner + estado paused; cualquier
          // tap (banner, BigPlayBtn o control bar) reanudará con audio activo.
          setShowStartOverlay(true);
          setVideoStatus('paused');
        });
      }).catch(() => {
        // El iframe nunca respondió (CDN/red): reintento o mensaje de error.
        fallaCarga();
      });

      // Events
      // Nota: NO ocultar el banner de "activar audio" aquí, porque iOS dispara
      // 'play' al iniciar autoplay muted. El banner solo desaparece cuando se
      // confirma audio activo (handleManualStart o togglePlayPause con unmute).
      playerRef.current.on('play', () => {
        setVideoStatus('playing');
      });

      playerRef.current.on('pause', (d) => {
        setVideoStatus('paused');
        if (!isReplay.current) saveProgress(d.seconds);
      });

      playerRef.current.on('timeupdate', (d) => {
        // Tiempo realmente reproducido: avances pequeños y continuos (un salto
        // con la barra no suma). Al repasar un video ya visto se envía solo el tiempo.
        const paso = d.seconds - (tiempoVimeo.current.ultimo ?? d.seconds);
        tiempoVimeo.current.ultimo = d.seconds;
        if (paso > 0 && paso <= 2) tiempoVimeo.current.pendiente += paso;
        if (isReplay.current && tiempoVimeo.current.pendiente >= 30) enviarTiempoVimeo();
        setCurrentTime(d.seconds);
        if (d.seconds > maxTimeReached.current) maxTimeReached.current = d.seconds;
        if (!isReplay.current && Math.abs(d.seconds - lastSavedTime.current) >= 10) {
          lastSavedTime.current = d.seconds;
          saveProgress(d.seconds);
        }
        // Barra de progreso en los controles del SO (Media Session).
        const dur = liveRef.current.videoDuration || 0;
        if ('mediaSession' in navigator && dur > 0 && navigator.mediaSession.setPositionState) {
          try {
            navigator.mediaSession.setPositionState({
              duration: dur, playbackRate: 1, position: Math.min(Math.max(0, d.seconds), dur),
            });
          } catch (e) {}
        }
      });

      playerRef.current.on('ended', () => {
        setVideoStatus('completed');
        if (!isReplay.current) {
          // forceCompleted ya marca completado=1; NO usar 9999 (centinela que
          // luego rompe el resume saltando al final). videoDuration aquí está
          // "stale" (closure capturado al registrar el handler) — el valor
          // confiable es el ref maxTimeReached.current (último punto visto).
          saveProgress(maxTimeReached.current || 0, true);
          handleVideoComplete();
        }
      });

      // Seek blocking (unless replay)
      playerRef.current.on('seeked', (d) => {
        if (!modoPruebasRef.current && !isReplay.current && d.seconds > maxTimeReached.current + 2) {
          playerRef.current.setCurrentTime(maxTimeReached.current).catch(() => {});
          setShowSeekWarning(true);
          setTimeout(() => setShowSeekWarning(false), 3000);
        }
      });

      // Estado del mini-reproductor (Picture-in-Picture)
      playerRef.current.on('enterpictureinpicture', () => setPipActive(true));
      playerRef.current.on('leavepictureinpicture', () => setPipActive(false));
    };

    // Load Vimeo SDK if needed, then init
    const t = setTimeout(() => {
      if (window.Vimeo) {
        initPlayer();
      } else {
        const script = document.createElement('script');
        script.src = 'https://player.vimeo.com/api/player.js';
        script.onload = initPlayer;
        // Si el propio SDK está bloqueado (adblock/firewall), fallar de inmediato
        script.onerror = () => {
          if (loadTimeoutRef.current) { clearTimeout(loadTimeoutRef.current); loadTimeoutRef.current = null; }
          setLoadError(true);
        };
        document.head.appendChild(script);
      }
    }, 150);

    return () => {
      clearTimeout(t);
      if (loadTimeoutRef.current) { clearTimeout(loadTimeoutRef.current); loadTimeoutRef.current = null; }
    };
  }, [playerKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetryLoad = () => {
    // Re-montar el iframe desde cero (limpia player previo y reintenta SDK)
    if (loadTimeoutRef.current) { clearTimeout(loadTimeoutRef.current); loadTimeoutRef.current = null; }
    autoRetryRef.current = 0;   // el usuario pide otro intento: contador a cero
    if (playerRef.current) {
      try { playerRef.current.pause(); } catch (e) { /* ignore */ }
      playerRef.current = null;
    }
    setLoadError(false);
    setIsPlayerReady(false); playerListoRef.current = false;
    setVideoStatus('loading');
    setPlayerKey(k => k + 1);
  };

  const handleManualStart = () => {
    if (!playerRef.current) return;
    // CRÍTICO: play() debe ejecutarse SÍNCRONO dentro del gesto. Un await antes
    // pierde la activación de usuario y móviles estrictos (Honor/Huawei) bloquean
    // la reproducción. setMuted/setVolume se disparan sin await (no consumen gesto).
    playerRef.current.setMuted(false).catch(() => {});
    playerRef.current.setVolume(1).catch(() => {});
    setIsMuted(false);
    setShowStartOverlay(false);
    playerRef.current.play()
      .then(() => setVideoStatus('playing'))
      .catch(e => { console.error('Error al iniciar:', e); setVideoStatus('paused'); });
  };

  // ─── CUSTOM CONTROLS ───
  const [showControls, setShowControls] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const controlsTimer = useRef(null);

  const toggleMute = () => {
    if (!playerRef.current) return;
    const next = !isMuted;
    playerRef.current.setMuted(next).catch(() => {});
    if (!next) playerRef.current.setVolume(1).catch(() => {});
    setIsMuted(next);
  };

  // ─── Picture-in-Picture (mini-ventana flotante sobre cualquier pestaña) ───
  const [pipActive, setPipActive] = useState(false);
  const pipSupported = typeof document !== 'undefined' && document.pictureInPictureEnabled;
  const togglePiP = () => {
    if (!playerRef.current) return;
    if (pipActive) playerRef.current.exitPictureInPicture().catch(() => {});
    else playerRef.current.requestPictureInPicture().catch(() => {});
  };

  // Al cambiar de pestaña: intento de PiP (best-effort: algunos navegadores
  // exigen que el usuario haya activado PiP con el botón antes) y, al volver,
  // reanudar si el navegador pausó el video por su cuenta.
  const sonabaAlOcultar = useRef(false);
  const videoStatusRef = useRef('loading');
  useEffect(() => { videoStatusRef.current = videoStatus; }, [videoStatus]);
  useEffect(() => {
    const onVis = () => {
      if (!playerRef.current) return;

      if (document.hidden) {
        sonabaAlOcultar.current = videoStatusRef.current === 'playing';
        if (!pipSupported || document.pictureInPictureElement) return;
        playerRef.current.getPaused()
          .then(paused => { if (!paused) playerRef.current.requestPictureInPicture().catch(() => {}); })
          .catch(() => {});
        return;
      }

      // De vuelta en la pestaña. Varios navegadores (móviles y Safari) pausan el
      // video de una pestaña en segundo plano; si el alumno lo dejó sonando, se
      // reanuda solo. Si él mismo lo había pausado, no se toca.
      if (!sonabaAlOcultar.current) return;
      playerRef.current.getPaused()
        .then(paused => {
          if (!paused) return;
          return playerRef.current.play().catch(() => {
            // Si el navegador exige un gesto para reanudar, al menos se lo
            // pedimos con el banner en vez de dejar el video mudo y quieto.
            setShowStartOverlay(true);
            setVideoStatus('paused');
          });
        })
        .catch(() => {});
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [pipSupported]);

  // ─── Mini-control flotante (Document Picture-in-Picture, sin el iframe) ───
  // Ventana propia con controles + mini-sidebar de lecciones + temporizador. El
  // video (y su AUDIO) se queda en la pestaña; esta ventanita flota sobre otras.
  const docPipSupported = typeof window !== 'undefined' && 'documentPictureInPicture' in window;
  const [miniOpen, setMiniOpen] = useState(false);
  const liveRef = useRef({});
  const pipWinRef = useRef(null);
  const pipTimerRef = useRef(null);
  const silentAudioRef = useRef(null); // ancla de Media Session (audio silencioso)
  useEffect(() => {
    liveRef.current = { videoStatus, isMuted, currentTime, videoDuration, mentorData };
  });

  const flatLessons = (md) => {
    const out = [];
    (md?.estructura?.modulos || []).forEach((m, mi) => {
      const modNum = m.numero || mi + 1;
      (m.lecciones || []).forEach((l, li) => {
        out.push({ modNum, lecNum: l.numero || li + 1, titulo: l.titulo || `Lección ${li + 1}` });
      });
    });
    return out;
  };
  const pipCurIdx = () => {
    const L = liveRef.current; const flats = flatLessons(L.mentorData);
    // Igual que arriba: el ⏮/⏭ del mini-reproductor se mueve desde la lección
    // que suena, no desde la del avance real.
    const md = L.mentorData;
    const modNum = md?.video_actual?.modulo_numero || md?.modulo_actual;
    const lecNum = md?.video_actual?.leccion_numero || md?.leccion_actual;
    return flats.findIndex(x => x.modNum === modNum && x.lecNum === lecNum);
  };
  const pipPlayPause = () => {
    if (!playerRef.current) return;
    if (liveRef.current.videoStatus === 'playing') { playerRef.current.pause(); return; }
    playerRef.current.setMuted(false).catch(() => {}); setIsMuted(false);
    playerRef.current.play().catch(() => {});
  };
  const pipMute = () => {
    if (!playerRef.current) return;
    const next = !liveRef.current.isMuted;
    playerRef.current.setMuted(next).catch(() => {});
    if (!next) playerRef.current.setVolume(1).catch(() => {});
    setIsMuted(next);
  };
  const pipRewind = () => {
    if (!playerRef.current) return;
    playerRef.current.setCurrentTime(Math.max(0, (liveRef.current.currentTime || 0) - 10)).catch(() => {});
  };
  const pipNav = (dir) => {
    const flats = flatLessons(liveRef.current.mentorData);
    const idx = pipCurIdx(); if (idx < 0) return;
    const target = flats[idx + dir]; if (!target) return;
    if (getLessonStatus(target.modNum, target.lecNum) === 'locked') return;
    loadLesson(target.modNum, target.lecNum);
  };
  // Ref siempre-actual a pipNav para que los handlers de Media Session (registrados
  // una sola vez al montar) no queden con un closure viejo de mentorData=null.
  const pipNavRef = useRef(pipNav);
  pipNavRef.current = pipNav;

  const closeMiniControl = () => { if (pipWinRef.current) { try { pipWinRef.current.close(); } catch (e) {} } };

  const openMiniControl = async () => {
    if (!docPipSupported) return;
    if (pipWinRef.current) { closeMiniControl(); return; } // toggle
    let win;
    try { win = await window.documentPictureInPicture.requestWindow({ width: 380, height: 250 }); }
    catch (e) { return; }
    pipWinRef.current = win; setMiniOpen(true);
    const d = win.document;
    const st = d.createElement('style');
    st.textContent = `
      *{box-sizing:border-box;margin:0;font-family:system-ui,-apple-system,sans-serif}
      body{background:#0b1220;color:#e2e8f0;padding:12px;user-select:none}
      .lz{padding:6px 8px;border-radius:8px;font-size:12px;line-height:1.3;margin-bottom:4px;display:flex;gap:6px;cursor:pointer;background:#141d2e}
      .lz .tag{color:#64748b;flex:0 0 auto}
      .lz span.t{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
      .lz.cur{background:#0891b2;color:#fff;cursor:default}
      .lz.disabled{opacity:.4;cursor:not-allowed}
      .lz:hover:not(.cur):not(.disabled){background:#1e293b}
      .bar{height:5px;border-radius:5px;background:rgba(255,255,255,.15);overflow:hidden;margin:8px 0 4px}
      .fill{height:100%;width:0;background:#06b6d4}
      .time{font-size:11px;color:#94a3b8;text-align:center;margin-bottom:8px}
      .ctrls{display:flex;gap:8px;justify-content:center;align-items:center}
      button{background:#1e293b;color:#e2e8f0;border:none;border-radius:10px;padding:8px 10px;font-size:16px;cursor:pointer;min-width:44px}
      button:hover{background:#334155}
      #pp{background:#06b6d4;color:#04121a;font-weight:700}
    `;
    d.head.appendChild(st);
    d.body.innerHTML = `
      <div id="prev" class="lz"><span class="tag">◀</span><span class="t"></span></div>
      <div id="cur" class="lz cur"><span class="tag">▶</span><span class="t"></span></div>
      <div id="next" class="lz"><span class="t"></span><span class="tag">▶</span></div>
      <div class="bar"><div id="fill" class="fill"></div></div>
      <div id="time" class="time">0:00 / 0:00</div>
      <div class="ctrls">
        <button id="rew" title="Retroceder 10s">⏪</button>
        <button id="pp" title="Play/Pausa">▶</button>
        <button id="mute" title="Silenciar">🔊</button>
      </div>`;
    const g = (id) => d.getElementById(id);
    g('pp').onclick = pipPlayPause;
    g('mute').onclick = pipMute;
    g('rew').onclick = pipRewind;
    g('prev').onclick = () => pipNav(-1);
    g('next').onclick = () => pipNav(1);
    const tick = () => {
      const L = liveRef.current; const flats = flatLessons(L.mentorData); const idx = pipCurIdx();
      const cur = idx >= 0 ? flats[idx] : null;
      const prev = idx > 0 ? flats[idx - 1] : null;
      const next = idx >= 0 ? flats[idx + 1] : null;
      g('cur').querySelector('.t').textContent = cur ? cur.titulo : '—';
      const pe = g('prev');
      if (prev) { pe.style.display = ''; pe.querySelector('.t').textContent = prev.titulo; } else pe.style.display = 'none';
      const ne = g('next');
      if (next) { ne.style.display = ''; ne.querySelector('.t').textContent = next.titulo; ne.classList.toggle('disabled', getLessonStatus(next.modNum, next.lecNum) === 'locked'); } else ne.style.display = 'none';
      const ct = L.currentTime || 0, dur = L.videoDuration || 0;
      g('time').textContent = `${formatTime(ct)} / ${formatTime(dur)}`;
      g('fill').style.width = dur > 0 ? `${Math.min(100, (ct / dur) * 100)}%` : '0%';
      g('pp').textContent = L.videoStatus === 'playing' ? '⏸' : '▶';
      g('mute').textContent = L.isMuted ? '🔇' : '🔊';
    };
    tick();
    pipTimerRef.current = setInterval(tick, 300);
    win.addEventListener('pagehide', () => {
      clearInterval(pipTimerRef.current); pipWinRef.current = null; setMiniOpen(false);
    });
  };

  // La ventana flotante 🗔 (Document PiP) es persistente: se abre con un clic y
  // permanece sobre TODAS las pestañas hasta que el usuario la cierra. No la
  // cerramos al volver a la pestaña (antes se cerraba y confundía). Los controles
  // del SO (Media Session) ya flotan sin necesidad de esta ventana.

  // Cerrar el mini-control y limpiar el timer al salir del reproductor.
  useEffect(() => () => {
    clearInterval(pipTimerRef.current);
    if (pipWinRef.current) { try { pipWinRef.current.close(); } catch (e) {} }
  }, []);

  // ─── MEDIA SESSION API ───────────────────────────────────────────────────
  // Controles de SO (pantalla de bloqueo, centro multimedia, teclas ⏯/⏭, auriculares)
  // conectados con el Vimeo SDK. Funcionan aunque el usuario esté en OTRA pestaña.
  // En iOS Safari NO usamos el ancla silenciosa (roba el foco de audio a Vimeo);
  // ahí el propio iframe de Vimeo ya expone controles nativos.
  const mediaSessionOK = typeof navigator !== 'undefined' && 'mediaSession' in navigator;
  const usaAncla = mediaSessionOK && !/iPad|iPhone|iPod/.test(typeof navigator !== 'undefined' ? navigator.userAgent : '');

  // (a) Registrar handlers UNA vez. Controlan el player real vía Vimeo SDK.
  useEffect(() => {
    if (!mediaSessionOK) return;
    const ms = navigator.mediaSession;
    const set = (accion, handler) => { try { ms.setActionHandler(accion, handler); } catch (e) {} };
    set('play', () => {
      if (!playerRef.current) return;
      playerRef.current.setMuted(false).catch(() => {}); setIsMuted(false);
      playerRef.current.play().catch(() => {});
    });
    set('pause', () => { if (playerRef.current) playerRef.current.pause().catch(() => {}); });
    set('stop', () => { if (playerRef.current) playerRef.current.pause().catch(() => {}); });
    set('seekbackward', (d) => {
      if (!playerRef.current) return;
      const off = (d && d.seekOffset) || 10;
      playerRef.current.setCurrentTime(Math.max(0, (liveRef.current.currentTime || 0) - off)).catch(() => {});
    });
    set('seekforward', (d) => {
      if (!playerRef.current) return;
      const off = (d && d.seekOffset) || 10;
      let t = (liveRef.current.currentTime || 0) + off;
      // Respetar el bloqueo de avance salvo en modo re-visión (replay).
      if (!isReplay.current) t = Math.min(t, maxTimeReached.current);
      playerRef.current.setCurrentTime(Math.max(0, t)).catch(() => {});
    });
    set('seekto', (d) => {
      if (!playerRef.current || !d || d.seekTime == null) return;
      let t = d.seekTime;
      if (!isReplay.current) t = Math.min(t, maxTimeReached.current + 2);
      playerRef.current.setCurrentTime(Math.max(0, t)).catch(() => {});
    });
    set('previoustrack', () => pipNavRef.current(-1));
    set('nexttrack', () => pipNavRef.current(1));
    return () => {
      ['play', 'pause', 'stop', 'seekbackward', 'seekforward', 'seekto', 'previoustrack', 'nexttrack']
        .forEach(a => { try { ms.setActionHandler(a, null); } catch (e) {} });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (b) Metadata (título de lección / programa / carátula) al cambiar de lección.
  useEffect(() => {
    if (!mediaSessionOK || !mentorData) return;
    const flats = flatLessons(mentorData);
    const idx = flats.findIndex(x => x.modNum === moduloEnPantalla && x.lecNum === leccionEnPantalla);
    const cur = idx >= 0 ? flats[idx] : null;
    const artwork = [];
    if (mentorData.document_logo) {
      const logo = String(mentorData.document_logo).startsWith('http')
        ? mentorData.document_logo : `${BACKEND_BASE}/${mentorData.document_logo}`;
      artwork.push({ src: logo, sizes: '512x512', type: 'image/png' });
    }
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: cur?.titulo || `Lección ${leccionEnPantalla}`,
        artist: mentorData.document_title || 'Mentoría',
        album: `Módulo ${moduloEnPantalla}`,
        artwork,
      });
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduloEnPantalla, leccionEnPantalla, mentorData?.document_title, mentorData?.document_logo]);

  // (c) Estado de reproducción para que el SO muestre ⏸ vs ▶.
  useEffect(() => {
    if (!mediaSessionOK) return;
    try { navigator.mediaSession.playbackState = videoStatus === 'playing' ? 'playing' : 'paused'; } catch (e) {}
  }, [videoStatus, mediaSessionOK]);

  // (d) Ancla de audio silencioso sincronizada con el video (no-iOS). Hace que la
  // sesión de medios activa sea la nuestra y no la del iframe de Vimeo.
  useEffect(() => {
    if (!usaAncla) return;
    const a = silentAudioRef.current;
    if (!a) return;
    if (videoStatus === 'playing') { a.play().catch(() => {}); }
    else { try { a.pause(); } catch (e) {} }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoStatus, usaAncla]);

  // Detener el ancla al desmontar.
  useEffect(() => () => {
    try { if (silentAudioRef.current) silentAudioRef.current.pause(); } catch (e) {}
    if (mediaSessionOK) { try { navigator.mediaSession.playbackState = 'none'; } catch (e) {} }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const togglePlayPause = () => {
    if (!playerRef.current) return;
    if (videoStatus === 'playing') {
      playerRef.current.pause();
      return;
    }
    // CRÍTICO: play() SÍNCRONO dentro del gesto. NO poner await antes — móviles
    // estrictos (Honor/Huawei) pierden la activación de usuario y bloquean el play.
    // setMuted(false) sin await recupera audio en iOS sin consumir el gesto.
    playerRef.current.setMuted(false).catch(() => {});
    setIsMuted(false);
    setShowStartOverlay(false);
    playerRef.current.play().catch(() => setVideoStatus('paused'));
  };

  const handleRewind10 = () => {
    if (!playerRef.current) return;
    const newTime = Math.max(0, currentTime - 10);
    playerRef.current.setCurrentTime(newTime).catch(() => {});
    // Retroceder es pedir que le repitan algo: el tutor se ofrece una vez.
    if (chatHabilitado && !quizActive && !avisoUsadoRef.current && chatMessages.length === 0) {
      avisoUsadoRef.current = true;
      setAvisoTutor(t('tutor_aviso_repaso'));
    }
  };

  const handleForward10 = () => {
    if (!playerRef.current || isReplay.current) return;
    // Only allow forward up to maxTimeReached
    const newTime = Math.min(maxTimeReached.current, currentTime + 10);
    playerRef.current.setCurrentTime(newTime).catch(() => {});
  };

  const handleProgressClick = (e) => {
    if (!playerRef.current || !videoDuration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const targetTime = pct * videoDuration;

    // If not replay, only allow seeking up to maxTimeReached (el modo pruebas
    // puede adelantar libremente).
    if (!modoPruebas && !isReplay.current && targetTime > maxTimeReached.current + 2) {
      setShowSeekWarning(true);
      setTimeout(() => setShowSeekWarning(false), 2000);
      return;
    }
    playerRef.current.setCurrentTime(targetTime).catch(() => {});
  };

  const handleVideoAreaClick = () => {
    togglePlayPause();
    // Show controls briefly
    setShowControls(true);
    clearTimeout(controlsTimer.current);
    controlsTimer.current = setTimeout(() => {
      if (videoStatus === 'playing') setShowControls(false);
    }, 3000);
  };

  const handleMouseMoveOnVideo = () => {
    setShowControls(true);
    clearTimeout(controlsTimer.current);
    controlsTimer.current = setTimeout(() => {
      if (videoStatus === 'playing') setShowControls(false);
    }, 3000);
  };

  const progressPercent = videoDuration > 0 ? (currentTime / videoDuration) * 100 : 0;
  const maxReachedPercent = videoDuration > 0 ? (maxTimeReached.current / videoDuration) * 100 : 0;

  // ─── SAVE PROGRESS ───
  // Envía solo el tiempo reproducido pendiente (repasos, cambio de lección, salida).
  const enviarTiempoVimeo = () => {
    const delta = Math.round(tiempoVimeo.current.pendiente);
    if (delta < 1 || !tiempoVimeo.current.videoId) return;
    tiempoVimeo.current.pendiente -= delta;
    mentor2Service.saveVideoProgress({ video_id: tiempoVimeo.current.videoId, document_id: parseInt(documentId), solo_tiempo: 1, tiempo_visto_delta: delta, timestamp_actual: 0 }).catch(() => {});
  };

  // Al cambiar de lección: lo pendiente va al video anterior y se empieza de cero.
  useEffect(() => {
    enviarTiempoVimeo();
    tiempoVimeo.current = { pendiente: 0, ultimo: null, videoId: videoActual?.vimeo_id ? videoActual.id : null };
  }, [videoActual?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Al ocultar o cerrar la pestaña.
  useEffect(() => {
    const alOcultar = () => { if (document.visibilityState === 'hidden') enviarTiempoVimeo(); };
    document.addEventListener('visibilitychange', alOcultar);
    return () => { document.removeEventListener('visibilitychange', alOcultar); enviarTiempoVimeo(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveProgress = async (timeInSeconds, forceCompleted = false) => {
    if (!videoActual?.id) return;
    const percentage = videoDuration > 0 ? (timeInSeconds / videoDuration) * 100 : 0;
    const isCompleted = forceCompleted || percentage >= 90;
    const tsActual = Math.round(timeInSeconds);
    const tsMaximo = Math.max(tsActual, Math.round(maxTimeReached.current));

    // Cache local: lo que se guarda aquí es lo que hay que reanudar si el alumno
    // se va a otra lección y vuelve, sin depender de recargar la página.
    const previo = progresoLocal.current[videoActual.id];
    progresoLocal.current[videoActual.id] = {
      timestamp_actual: tsActual,
      timestamp_maximo: Math.max(tsMaximo, previo?.timestamp_maximo || 0),
      completado: Math.max(isCompleted ? 1 : 0, previo?.completado || 0)
    };

    try {
      const delta = Math.round(tiempoVimeo.current.pendiente);
      tiempoVimeo.current.pendiente -= delta;
      await mentor2Service.saveVideoProgress({
        video_id: videoActual.id,
        document_id: parseInt(documentId),
        timestamp_actual: tsActual,
        timestamp_maximo: tsMaximo,
        completado: isCompleted ? 1 : 0,
        tiempo_visto_delta: delta,
      });
    } catch (e) {
      console.error('Error guardando progreso:', e);
    }
  };

  // ─── CONTENIDO INTERACTIVO ───
  // No hay reproductor que avise el final: el alumno lo marca al terminarlo.
  // Se completa sin preguntas de repaso (no hay transcripción de la que salgan).
  const completarInteractivo = async () => {
    if (!videoActual?.id) return;
    await saveProgress(0, true);
    // Marcarlo también en el contenido actual: «Siguiente» se habilita sin recargar.
    setMentorData(prev => prev ? {
      ...prev,
      video_actual: prev.video_actual && prev.video_actual.id === videoActual.id ? { ...prev.video_actual, completado: 1 } : prev.video_actual,
      videos: (prev.videos || []).map(v => (v.video_id === videoActual.id ? { ...v, completado: 1 } : v)),
    } : prev);
    isReplay.current = false;
    setQuizActive(false);
    setQuizCompleto(true);
  };

  // Interactivo que terminó solo (fase 4): queda marcado como visto y, como un
  // video, abre las preguntas de repaso (fase 6: salen del texto del paquete).
  // Sin retroalimentación en el curso, se cierra la lección sin preguntas.
  const completarInteractivoAuto = async () => {
    if (!videoActual?.id) return;
    await saveProgress(0, true);
    setMentorData(prev => prev ? {
      ...prev,
      video_actual: prev.video_actual && prev.video_actual.id === videoActual.id ? { ...prev.video_actual, completado: 1 } : prev.video_actual,
      videos: (prev.videos || []).map(v => (v.video_id === videoActual.id ? { ...v, completado: 1 } : v)),
    } : prev);
    isReplay.current = false;
    handleVideoComplete();
  };

  // ─── VIDEO COMPLETE → QUIZ ───
  const handleVideoComplete = () => {
    if (!videoActual?.id) return;
    // En modo replay (video ya completado) no se re-dispara la retroalimentación.
    if (isReplay.current) return;
    if (realtimeActive) disconnectRealtime();
    // Si el documento tiene las preguntas de retroalimentación deshabilitadas,
    // se completa la lección sin quiz (NO mostramos "generando preguntas de repaso").
    if (mentorData?.retroalimentacion === 0) {
      setQuizActive(false);
      setQuizCompleto(true);
      setChatMessages(prev => [...prev, { role: 'ai', text: t('mentor_leccion_completada_simple') }]);
      return;
    }
    abrirTutor(true, false);
    setChatMessages(prev => [...prev, {
      role: 'ai',
      text: t('mentor_leccion_completada')
    }]);
    generateQuiz();
  };

  const generateQuiz = async () => {
    try {
      setChatLoading(true);
      setQuizActive(true);
      setQuizPregunta(1);

      const res = await mentor2Service.generateQuiz(documentId, videoActual.id);
      const data = res.data?.data || res.data;

      setChatMessages(prev => [...prev, { role: 'ai', text: data.mensaje }]);
    } catch (e) {
      console.error('Error generando quiz:', e);
      setChatMessages(prev => [...prev, {
        role: 'ai',
        text: 'No se pudieron generar las preguntas. Puedes avanzar a la siguiente lección.'
      }]);
      setQuizCompleto(true);
      setQuizActive(false);
    } finally {
      setChatLoading(false);
    }
  };

  // ─── REALTIME VOICE ───
  const connectRealtime = useCallback(async () => {
    if (realtimeSessionRef.current) return;
    setRealtimeError('');
    setRealtimeMicUnavailable(false);
    setRealtimePartial('');
    realtimeAiBuffer.current = '';
    realtimeTranscriptsRef.current = [];

    // Pausar video para que el mic no capte el audio
    if (playerRef.current && videoStatus === 'playing') {
      playerRef.current.pause().catch(() => {});
    }

    // Abrir el panel al conectar la voz (el alumno puede cerrarlo después)
    abrirTutor(true, false);

    const session = new RealtimeSession({
      documentId: parseInt(documentId),
      mode: 'mentor',
      videoId: videoActual?.id,
      videoTitle: videoActual?.titulo_completo,
      lessonContext: mentorData ? `Módulo ${moduloEnPantalla} - Lección ${leccionEnPantalla}` : null,
      currentTime: Math.round(currentTime),
      // Posición en el programa: con esto el tutor de voz sabe dónde está el
      // alumno y puede responder qué le falta, igual que el chat de texto.
      extraSessionPayload: { modulo_actual: moduloEnPantalla, leccion_actual: leccionEnPantalla },
      autoGreeting: false, // que NO salude al conectar
      autoStart: false,    // y que NO genere respuesta inicial: espera a que el alumno pregunte

      onStateChange: (state) => setRealtimeState(state),
      onTranscript: (text) => {
        setChatMessages(prev => [...prev, { role: 'user', text }]);
        realtimeTranscriptsRef.current.push(text);
      },
      onAITranscript: (text, isDone) => {
        if (isDone) {
          setChatMessages(prev => [...prev, { role: 'ai', text }]);
          setRealtimePartial('');
          realtimeAiBuffer.current = '';
        } else {
          realtimeAiBuffer.current += text;
          setRealtimePartial(realtimeAiBuffer.current);
        }
      },
      onError: (msg) => {
        setRealtimeError(msg);
        if (msg === 'REALTIME_NOT_AVAILABLE') {
          setRealtimeActive(false);
        }
      },
      onMicError: () => { setRealtimeMicUnavailable(true); },
      onAudioLevel: (level) => setRealtimeAudioLevel(level)
    });

    realtimeSessionRef.current = session;
    setRealtimeActive(true);
    await session.connect();
  }, [documentId, videoActual?.id, videoActual?.titulo_completo, moduloEnPantalla, leccionEnPantalla, currentTime, videoStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveRealtimeTranscripts = useCallback(async () => {
    const transcripts = realtimeTranscriptsRef.current;
    if (!transcripts || transcripts.length === 0) return;
    try {
      await realtimeSessionService.saveTranscripts(
        documentId,
        transcripts.map(text => ({ text })),
        0,
        'mentor'
      );
    } catch (e) {
      console.error('Error guardando transcripciones:', e);
    }
  }, [documentId]);

  const disconnectRealtime = useCallback(() => {
    saveRealtimeTranscripts();
    if (realtimeSessionRef.current) {
      realtimeSessionRef.current.disconnect();
      realtimeSessionRef.current = null;
    }
    setRealtimeActive(false);
    setRealtimeState(SESSION_STATES.DISCONNECTED);
    setRealtimePartial('');
    realtimeAiBuffer.current = '';
  }, [saveRealtimeTranscripts]);

  const toggleRealtime = () => {
    if (realtimeActive) disconnectRealtime();
    else connectRealtime();
  };

  const getRealtimeLabel = () => {
    switch (realtimeState) {
      case SESSION_STATES.CONNECTING: return t('mentor_realtime_conectando');
      case SESSION_STATES.CONNECTED: return t('mentor_realtime_conectado');
      case SESSION_STATES.LISTENING: return t('mentor_realtime_escuchando');
      case SESSION_STATES.AI_SPEAKING: return t('mentor_realtime_respondiendo');
      case SESSION_STATES.ERROR: return t('mentor_realtime_error');
      default: return '';
    }
  };

  // Cleanup realtime on unmount or video change
  useEffect(() => {
    return () => {
      if (realtimeSessionRef.current) {
        realtimeSessionRef.current.disconnect();
        realtimeSessionRef.current = null;
      }
    };
  }, []);

  // ─── CHAT / QUIZ SUBMIT ───
  const submitMessage = async (msg) => {
    abrirTutor(true);
    marcarTutorConocido();
    setChatMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatInput('');
    setChatLoading(true);

    try {
      if (quizActive) {
        const res = await mentor2Service.evaluateQuiz(
          documentId, videoActual.id, quizPregunta, msg
        );
        const data = res.data?.data || res.data;
        setChatMessages(prev => [...prev, { role: 'ai', text: data.mensaje }]);
        if (data.quiz_completo) { setQuizActive(false); setQuizCompleto(true); }
        else { setQuizPregunta(data.pregunta_actual); }
      } else {
        const res = await mentor2Service.chat({
          documentId: parseInt(documentId),
          videoId: videoActual.id,
          pregunta: msg,
          currentTime,
          moduloActual: moduloEnPantalla,
          leccionActual: leccionEnPantalla
        });
        const data = res.data?.data || res.data;
        setChatMessages(prev => [...prev, { role: 'ai', text: data.respuesta }]);
      }
    } catch (e) {
      console.error('Error en chat:', e);
      setChatMessages(prev => [...prev, {
        role: 'ai',
        text: t('mentor_error_procesar_mensaje')
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    const msg = chatInput.trim();
    if (!msg || chatLoading) return;
    submitMessage(msg);
  };

  // ─── EL TUTOR: cara, sugerencias y señales de vida ───

  // La cara del tutor es la del docente de la lección y, si no tiene, la del
  // módulo: es lo que convierte un cuadro de «Chat» en alguien a quien
  // preguntarle. Los mismos datos que ya muestra el índice de lecciones.
  const docenteTutor = useMemo(() => {
    const mod = mentorData?.estructura?.modulos?.[(moduloEnPantalla || 1) - 1];
    const lec = mod?.lecciones?.[(leccionEnPantalla || 1) - 1];
    const d = (lec?.docente_foto || lec?.docente_nombre) ? lec
      : ((mod?.docente_foto || mod?.docente_nombre) ? mod : null);
    if (!d) return null;
    return {
      nombre: d.docente_nombre,
      foto: resolveDocenteFoto(d.docente_foto),
      bandera: flagFromPais(d.docente_pais),
    };
  }, [mentorData, moduloEnPantalla, leccionEnPantalla]);

  const marcarTutorConocido = useCallback(() => {
    setTutorDestacado(false);
    localStorage.setItem('mentor-tutor-conocido', 'true');
  }, []);

  // Preguntas de la lección: el backend las genera una sola vez y las guarda,
  // así que esto no cuesta una llamada al modelo por alumno.
  useEffect(() => {
    if (!videoActual?.id || !chatHabilitado) { setSugerencias([]); return; }
    let vivo = true;
    mentor2Service.sugerencias(parseInt(documentId), videoActual.id)
      .then(res => { if (vivo) setSugerencias(res.data?.data?.sugerencias || []); })
      .catch(() => { if (vivo) setSugerencias([]); });
    return () => { vivo = false; };
  }, [documentId, videoActual?.id, chatHabilitado, idiomaPlataforma]);

  // Al cambiar de lección el tutor vuelve a tener una oferta disponible.
  useEffect(() => {
    avisoUsadoRef.current = false;
    setAvisoTutor(null);
  }, [videoActual?.id]);

  // Se muestran dos preguntas del tramo que va viendo y una del programa, para
  // que descubra que el tutor también sabe cómo va su certificación. El tramo
  // se redondea a cuartos: las sugerencias no pueden bailar mientras ve el video.
  const tramoVideo = videoDuration > 0 ? Math.floor((currentTime / videoDuration) * 4) / 4 : 0;
  const sugerenciasVisibles = useMemo(() => {
    if (chatMessages.length > 0 || quizActive) return [];
    const cercanas = [...sugerencias]
      .sort((a, b) => Math.abs((a.momento ?? 0) - tramoVideo) - Math.abs((b.momento ?? 0) - tramoVideo))
      .slice(0, 2)
      .map(s => s.p);
    return [...cercanas, t('tutor_sugerencia_certificado')];
  }, [sugerencias, tramoVideo, chatMessages.length, quizActive, t]);

  const enviarSugerencia = (texto) => {
    setAvisoTutor(null);
    submitMessage(texto);
  };

  // Señal de vida: si se queda en pausa un rato sin haber preguntado nada, el
  // tutor se ofrece UNA vez. Es el momento en que el alumno se está trabando.
  useEffect(() => {
    if (!chatHabilitado || quizActive || avisoUsadoRef.current) return;
    if (videoStatus !== 'paused' || chatMessages.length > 0) return;
    const id = setTimeout(() => {
      avisoUsadoRef.current = true;
      setAvisoTutor(t('tutor_aviso_pausa'));
    }, 8000);
    return () => clearTimeout(id);
  }, [videoStatus, chatHabilitado, quizActive, chatMessages.length, t]);

  // ─── NAVIGATION ───
  const handleAdvance = async () => {
    try {
      setLoading(true);
      destroyPlayer();
      setChatMessages([]);
      setQuizActive(false);
      setQuizCompleto(false);
      setQuizPregunta(0);

      const res = await mentor2Service.advance(documentId);
      const data = res.data?.data || res.data;
      setMentorData(data);

      if (data.modulo_actual) {
        setExpandedModules(prev => ({ ...prev, [data.modulo_actual]: true }));
      }
    } catch (e) {
      console.error('Error al avanzar:', e);
      setError(t('mentor_error_avanzar'));
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (!mentorData?.estructura?.modulos) return;
    const estructura = mentorData.estructura;

    // "Anterior" se mueve desde la lección que se está viendo, no desde la del
    // avance: en repaso saltaba a la anterior a la última alcanzada.
    let prevMod = moduloEnPantalla;
    let prevLec = leccionEnPantalla - 1;

    if (prevLec < 1) {
      prevMod = moduloEnPantalla - 1;
      if (prevMod < 1) return;
      const modInfo = estructura.modulos[prevMod - 1];
      prevLec = modInfo?.lecciones?.length || 1;
    }

    loadLesson(prevMod, prevLec);
  };

  const loadLesson = async (moduleNum, lessonNum) => {
    try {
      // Guardar dónde va el video que se deja (destroyPlayer quita el listener
      // de 'pause' antes de que Vimeo lo dispare, y el autoguardado va cada 10s:
      // sin esto se pierde el último tramo visto al cambiar de lección).
      if (!isReplay.current && currentTime > 0) {
        saveProgress(currentTime);
      }

      setLoading(true);
      destroyPlayer();
      setChatMessages([]);
      setQuizActive(false);
      setQuizCompleto(false);

      const res = await mentor2Service.getVideoForLesson(documentId, moduleNum, lessonNum);
      // video-leccion.php returns { success, video: { id, titulo, vimeo_id, ... } }
      const videoData = res.data?.video || res.data?.data || res.data;

      if (!videoData?.id) {
        setError(t('mentor_error_video_no_encontrado'));
        setLoading(false);
        return;
      }

      // Normalize field name (video-leccion returns titulo, start returns titulo_completo)
      if (!videoData.titulo_completo && videoData.titulo) {
        videoData.titulo_completo = videoData.titulo;
      }

      // Progreso a reanudar. Tres fuentes, de más a menos fresca:
      //   1. progresoLocal: lo visto en esta sesión (aún no reflejado en `videos`)
      //   2. videoData: lo que acaba de devolver video-leccion.php (BD)
      //   3. mentorData.videos: la foto del arranque (fallback)
      const videoInfo = mentorData.videos?.find(v => v.video_id === videoData.id);
      const local = progresoLocal.current[videoData.id];

      const tsActual = local
        ? local.timestamp_actual
        : (Number(videoData.timestamp_actual) || videoInfo?.timestamp_actual || 0);
      const tsMaximo = Math.max(
        Number(videoData.timestamp_maximo) || 0,
        videoInfo?.timestamp_maximo || 0,
        local?.timestamp_maximo || 0
      );
      const completado = Math.max(
        Number(videoData.completado) || 0,
        videoInfo?.completado || 0,
        local?.completado || 0
      );

      // Check if this is a completed video (replay mode)
      isReplay.current = !!completado;

      setMentorData(prev => ({
        ...prev,
        // Mantener el sidebar al día con el progreso real de esta lección.
        videos: (prev.videos || []).map(v => v.video_id === videoData.id
          ? { ...v, timestamp_actual: tsActual, timestamp_maximo: tsMaximo, completado }
          : v),
        video_actual: {
          ...videoData,
          timestamp_actual: tsActual,
          timestamp_maximo: tsMaximo,
          completado
        },
        // Al REPRODUCIR un video ya completado (replay) NO movemos el puntero de
        // progreso real: solo cambiamos el video que se ve. Mover modulo_actual/
        // leccion_actual hacia atrás bloqueaba/deshabilitaba la lección real actual.
        ...(isReplay.current ? {} : { modulo_actual: moduleNum, leccion_actual: lessonNum })
      }));

      setSidebarOpen(false);
    } catch (e) {
      console.error('Error cargando lección:', e);
      setError(t('mentor_error_cargar_leccion'));
    } finally {
      setLoading(false);
    }
  };

  // ─── SIDEBAR HELPERS ───
  const toggleModule = (modNum) => {
    setExpandedModules(prev => ({ ...prev, [modNum]: !prev[modNum] }));
  };

  const getLessonStatus = (modNum, lecNum) => {
    if (!mentorData?.videos) return 'locked';
    const video = mentorData.videos.find(
      v => v.modulo_numero === modNum && v.leccion_numero === lecNum
    );
    if (video?.completado) return 'completed';
    if (modNum === mentorData.modulo_actual && lecNum === mentorData.leccion_actual) return 'current';

    // Allow access to completed + current lessons
    const isBeforeCurrent =
      modNum < mentorData.modulo_actual ||
      (modNum === mentorData.modulo_actual && lecNum < mentorData.leccion_actual);

    if (isBeforeCurrent) return 'completed';
    // Usuario de pruebas: la lección queda abierta (ni completada ni bloqueada).
    return modoPruebas ? 'disponible' : 'locked';
  };

  const getModuleStatus = (modNum) => {
    if (!mentorData?.estructura?.modulos) return 'locked';
    const modInfo = mentorData.estructura.modulos[modNum - 1];
    if (!modInfo) return 'locked';

    const allCompleted = modInfo.lecciones.every(
      (_, i) => getLessonStatus(modNum, i + 1) === 'completed'
    );
    if (allCompleted) return 'completed';
    if (modNum === mentorData.modulo_actual) return 'current';
    if (modNum < mentorData.modulo_actual) return 'completed';
    return 'locked';
  };

  // ─── ANCLA A UN MÓDULO (?modulo=) ───
  // La tarjeta de un módulo del catálogo manda su id acá para abrir el curso
  // justo en ese módulo. Se aplica UNA vez por visita (la ref), así que recargar
  // los datos —avanzar, modo pruebas— no vuelve a mover al alumno de sitio.
  const anclaAplicada = useRef(false);
  useEffect(() => {
    if (anclaAplicada.current) return;
    const mods = mentorData?.estructura?.modulos;
    if (!mods?.length) return;
    anclaAplicada.current = true;

    const pedido = searchParams.get('modulo');
    if (!pedido) {
      // Sin módulo pedido: la última lección que abrió (al recargar o volver a
      // entrar), aunque sea un repaso anterior al punto de avance.
      const uv = mentorData.ultima_vista;
      if (!uv || getLessonStatus(uv.modulo, uv.leccion) === 'locked') return;
      if (uv.modulo === moduloEnPantalla && uv.leccion === leccionEnPantalla) return;
      setExpandedModules(prev => ({ ...prev, [uv.modulo]: true }));
      loadLesson(uv.modulo, uv.leccion);
      return;
    }
    // El módulo pedido se aplica una vez: se quita de la URL para que al
    // recargar mande la última lección abierta y no otra vez este módulo.
    const sinAncla = new URLSearchParams(searchParams);
    sinAncla.delete('modulo');
    sinAncla.delete('leccion');
    setSearchParams(sinAncla, { replace: true });
    // Se acepta el id real del módulo y, como respaldo, su número de orden.
    let idx = mods.findIndex(m => String(m.modulo_id) === String(pedido));
    if (idx < 0) {
      const n = parseInt(pedido, 10);
      if (n >= 1 && n <= mods.length) idx = n - 1;
    }
    if (idx < 0) return;

    const modNum = mods[idx].numero || idx + 1;
    setExpandedModules(prev => ({ ...prev, [modNum]: true }));

    // Dentro del módulo: la lección pedida (?leccion=, p. ej. desde la página
    // del curso) si está abierta; si no, la primera abierta que le falte por ver
    // y, si ya las vio todas, la primera (repaso). Si el módulo entero sigue
    // bloqueado no se toca nada: el alumno se queda donde iba.
    const lecs = mods[idx].lecciones || [];
    let destino = -1;
    const leccionPedida = parseInt(searchParams.get('leccion'), 10);
    if (leccionPedida >= 1 && leccionPedida <= lecs.length && getLessonStatus(modNum, leccionPedida) !== 'locked') {
      destino = leccionPedida - 1;
    }
    for (let i = 0; destino < 0 && i < lecs.length; i++) {
      const st = getLessonStatus(modNum, i + 1);
      if (st === 'locked') continue;
      if (destino < 0) destino = i;
      if (st !== 'completed') { destino = i; break; }
    }
    if (destino < 0) return;
    if (modNum === moduloEnPantalla && (destino + 1) === leccionEnPantalla) return;
    loadLesson(modNum, destino + 1);
  }, [mentorData, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lección en pantalla y si su entrega de archivos (si la pide) aún no está aprobada.
  const leccionEnPantallaObj = mentorData?.estructura?.modulos?.[(moduloEnPantalla || 1) - 1]?.lecciones?.[(leccionEnPantalla || 1) - 1];
  // (Si ya estaba completada —p. ej. la entrega se activó después—, no se bloquea.)
  const entregaPendiente = !!leccionEnPantallaObj?.entrega
    && !['aprobada', 'automatica'].includes(entregaEstados[leccionEnPantallaObj.leccion_id])
    && !(mentorData?.videos || []).some(v => v.video_id === leccionEnPantallaObj.media_id && v.completado);

  const canAdvance = () => {
    if (mentorData?.estado === 'completado') return false;
    // Modo pruebas: se puede pasar de lección sin ver el video ni el quiz.
    if (modoPruebas) return true;
    // La lección se completa cuando la entrega queda aprobada (o enviada, si es automática).
    if (entregaPendiente) return false;
    // Video must be completed and quiz must be completed
    const videoCompleted = videoActual?.completado || videoStatus === 'completed';
    return videoCompleted && (quizCompleto || isReplay.current);
  };

  const canGoPrevious = () => {
    if (!mentorData) return false;
    return moduloEnPantalla > 1 || leccionEnPantalla > 1;
  };

  // ¿Existe una lección posterior a la actual? (para el resplandor/capa de avanzar)
  const haySiguienteLeccion = () => {
    const flats = flatLessons(mentorData);
    const idx = flats.findIndex(x => x.modNum === mentorData?.modulo_actual && x.lecNum === mentorData?.leccion_actual);
    return idx >= 0 && idx < flats.length - 1;
  };

  // Lección lista para avanzar por PRIMERA vez (no replay): resplandor + capa.
  const avanzarListo = quizCompleto && !isReplay.current && mentorData?.estado !== 'completado' && haySiguienteLeccion();

  // ÚLTIMA lección completada por primera vez (no replay): felicitación + salida.
  const programaListo = quizCompleto && !isReplay.current && !haySiguienteLeccion();

  // ─── EXAMEN DEL PROGRAMA ───
  // El contenido puede llevar su examen dentro del modo mentor (se activa en la
  // ficha del contenido). El botón se ve siempre y se habilita cuando el alumno
  // termina TODAS las lecciones, no solo la última que abrió.
  //
  // Si el curso tiene exámenes asociados (a una lección, un módulo o el curso),
  // cada uno aparece en su lugar del menú y el botón de abajo es el del curso.
  const examenes = mentorData?.examenes || [];
  const examenCurso = examenes.find(e => e.alcance === 'curso') || null;
  const examenEnPrograma = examenes.length ? !!examenCurso : !!mentorData?.examen_en_programa;
  const leccionesPendientes = (mentorData?.videos || []).filter(v => !v.completado).length;
  const examenHabilitado = examenEnPrograma && (
    mentorData?.estado === 'completado' ||
    programaListo ||
    ((mentorData?.videos || []).length > 0 && leccionesPendientes === 0)
  );

  const irAExamen = (examenId) => {
    if (!isReplay.current && currentTime > 0) saveProgress(currentTime);
    navigate(`/evaluacion/${documentId}${examenId ? `?examen=${examenId}` : ''}`);
  };
  const irAlExamen = () => {
    if (!examenHabilitado) return;
    irAExamen(examenCurso?.id);
  };

  // Estado de un examen: se abre al completar las lecciones de su alcance.
  const estadoExamen = (ex) => {
    if (ex.aprobado) return 'aprobado';
    if (ex.max_intentos > 0 && ex.intentos >= ex.max_intentos) return 'agotado';
    if (modoPruebas) return 'disponible';
    const mods = mentorData?.estructura?.modulos || [];
    const listas = (mod, mi) => (mod.lecciones || []).every((l, li) => getLessonStatus(mod.numero || mi + 1, l.numero || li + 1) === 'completed');
    if (ex.alcance === 'leccion') {
      for (let mi = 0; mi < mods.length; mi++) {
        const li = (mods[mi].lecciones || []).findIndex(l => l.leccion_id === ex.leccion_id);
        if (li >= 0) return getLessonStatus(mods[mi].numero || mi + 1, mods[mi].lecciones[li].numero || li + 1) === 'completed' ? 'disponible' : 'bloqueado';
      }
      return 'bloqueado';
    }
    if (ex.alcance === 'modulo') {
      const mi = mods.findIndex(m => m.modulo_id === ex.modulo_id);
      return mi >= 0 && listas(mods[mi], mi) ? 'disponible' : 'bloqueado';
    }
    return examenHabilitado ? 'disponible' : 'bloqueado';
  };

  const filaExamen = (ex) => {
    const estado = estadoExamen(ex);
    const detalle = estado === 'aprobado'
      ? `Aprobado${ex.mejor != null ? ` · ${ex.mejor} %` : ''}`
      : estado === 'agotado'
        ? `Sin intentos${ex.mejor != null ? ` · mejor ${ex.mejor} %` : ''}`
        : estado === 'bloqueado'
          ? (ex.alcance === 'leccion' ? 'Completa la lección para presentarlo' : ex.alcance === 'modulo' ? 'Completa el módulo para presentarlo' : 'Completa el curso para presentarlo')
          : `${ex.preguntas_por_intento} preguntas · apruebas con ${Math.round(ex.porcentaje_aprobacion)} %${ex.intentos ? ` · mejor ${ex.mejor} %` : ''}`;
    return (
      <ExamenItem
        key={`ex-${ex.id}`}
        type="button"
        $estado={estado}
        $nivel={ex.alcance}
        disabled={estado === 'bloqueado' || estado === 'agotado'}
        onClick={() => irAExamen(ex.id)}
        title={estado === 'aprobado' ? 'Puedes repasarlo cuando quieras' : undefined}
      >
        <span className="ico"><IcoExamen estado={estado} /></span>
        <span className="txt">
          <strong>{ex.titulo}</strong>
          <small>{detalle}</small>
        </span>
      </ExamenItem>
    );
  };

  // Al quedar lista, mostrar la capa superpuesta (una vez; se puede cerrar).
  useEffect(() => {
    if (avanzarListo) setShowAdvanceOverlay(true);
    else setShowAdvanceOverlay(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avanzarListo]);

  useEffect(() => {
    if (programaListo) setShowCongratsOverlay(true);
    else setShowCongratsOverlay(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programaListo]);

  // Marcar el programa como completado en backend y volver a los contenidos.
  // ─── Compartir en LinkedIn ───
  // Se abre en ventana emergente: el alumno publica y, al cerrarla, el curso
  // sigue solo con la siguiente lección. En el celular (pestaña nueva o app)
  // se detecta el regreso a la página.
  const [compartiendo, setCompartiendo] = useState(false);
  const [textoCopiado, setTextoCopiado] = useState(false);
  const compartirVigiaRef = useRef(null);

  const detenerVigiaCompartir = useCallback(() => {
    const v = compartirVigiaRef.current;
    if (!v) return;
    clearInterval(v.intervalo);
    document.removeEventListener('visibilitychange', v.alVolver);
    compartirVigiaRef.current = null;
  }, []);

  useEffect(() => detenerVigiaCompartir, [detenerVigiaCompartir]);

  const compartirEnLinkedIn = (esCurso = false, alTerminar = null) => {
    const curso = tlTitulo(mentorData?.estructura, 'titulo_programa') || mentorData?.document_title || '';
    const lecEstructura = mentorData?.estructura?.modulos?.[moduloEnPantalla - 1]?.lecciones?.[leccionEnPantalla - 1];
    const leccion = tlTitulo(lecEstructura) || videoActual?.titulo_completo || '';
    // Por defecto, la página del curso: su vista previa (título, imagen) la arma seo.php.
    const url = (compartirCfg.url || '').trim() || `${window.location.origin}/mentor/${documentId}?rec=1`;
    const plantilla = (compartirCfg.texto || '').trim() && !esCurso
      ? compartirCfg.texto
      : t(esCurso ? 'mentor_compartir_texto_curso' : 'mentor_compartir_texto_leccion');
    const texto = plantilla.replace(/\{leccion\}/g, leccion).replace(/\{curso\}/g, curso);
    // LinkedIn solo arma la tarjeta con miniatura cuando recibe el enlace por
    // share-offsite, y ahí no acepta texto: el texto va al portapapeles para
    // que el alumno lo pegue en su publicación.
    const destino = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    // Celular: el menú nativo de compartir le pasa texto y enlace juntos a la
    // app de LinkedIn (publicación escrita + tarjeta, sin pegar nada). Al
    // terminar vuelve aquí y sigue con la siguiente lección; si cancela, se queda.
    const esTactil = window.matchMedia?.('(pointer: coarse)').matches;
    if (esTactil && navigator.share) {
      navigator.share({ text: texto, url })
        .then(() => { if (alTerminar) alTerminar(); })
        .catch(() => { /* canceló: se queda en la capa */ });
      return;
    }
    const copiarTexto = () => {
      try {
        const area = document.createElement('textarea');
        area.value = texto;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        document.body.removeChild(area);
      } catch (e) { /* sin portapapeles: la tarjeta sale igual */ }
    };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(texto).catch(copiarTexto);
    else copiarTexto();
    setTextoCopiado(true);

    const ancho = 600, alto = 680;
    const izq = Math.max(0, (window.screenX || 0) + (window.outerWidth - ancho) / 2);
    const arriba = Math.max(0, (window.screenY || 0) + (window.outerHeight - alto) / 2);
    const ventana = window.open(destino, 'compartir-linkedin',
      `width=${ancho},height=${alto},left=${izq},top=${arriba}`);

    if (!alTerminar) return;
    detenerVigiaCompartir();
    setCompartiendo(true);
    let seFue = false;
    const terminar = () => {
      detenerVigiaCompartir();
      setCompartiendo(false);
      alTerminar();
    };
    const alVolver = () => {
      if (document.visibilityState === 'hidden') seFue = true;
      else if (seFue) terminar();
    };
    document.addEventListener('visibilitychange', alVolver);
    const intervalo = setInterval(() => {
      if (ventana && ventana.closed) terminar();
    }, 700);
    compartirVigiaRef.current = { intervalo, alVolver };
  };

  const avanzarTrasCompartir = () => {
    setShowAdvanceOverlay(false);
    handleAdvance();
  };

  const finalizarPrograma = async () => {
    try { await mentor2Service.advance(documentId); } catch (e) { /* no bloquear la salida */ }
    navigate(volverA.current);
  };

  // ─── RENDER ───
  if (loading && !mentorData) {
    return (
      <PageWrapper $light={lightMode} style={{ display: 'block' }}>
        <LoadingScreen>Cargando programa de estudio...</LoadingScreen>
      </PageWrapper>
    );
  }

  // Pantalla "Próximamente" reutilizable (sin contenido mentor configurado).
  const proximamenteScreen = (
    <PageWrapper $light={lightMode} style={{ display: 'block' }}>
      <ErrorScreen>
        <div style={{ fontSize: '2.6rem' }}>🚧</div>
        <h2 style={{ margin: '0.4rem 0 0.6rem' }}>{t('mentor_proximamente')}</h2>
        <p style={{ maxWidth: 480, margin: '0 auto 1rem', lineHeight: 1.5 }}>
          {t('mentor_proximamente_msg')}
        </p>
        <NavBtn onClick={() => navigate(volverA.current)}>{t('mentor_volver_a_contenidos')}</NavBtn>
      </ErrorScreen>
    </PageWrapper>
  );

  if (bloqueo) return <BloqueoSuscripcion code={bloqueo.code} suscripcion={bloqueo.suscripcion} volver={volverA.current || '/'} />;

  if (error && !mentorData) {
    // El backend devuelve "...no tiene contenido/videos mentor configurado(s)" cuando el
    // documento aún no tiene material → mostrar "Próximamente" en vez del error técnico.
    if (/no tiene.*mentor|mentor.*configurad|configurad/i.test(error)) {
      return proximamenteScreen;
    }
    return (
      <PageWrapper $light={lightMode} style={{ display: 'block' }}>
        <ErrorScreen>
          <div style={{ fontSize: '2rem' }}>!</div>
          <p>{error}</p>
          <NavBtn onClick={() => navigate(volverA.current)}>{t('mentor_volver_a_contenidos')}</NavBtn>
        </ErrorScreen>
      </PageWrapper>
    );
  }

  if (!mentorData) return null;

  const { estructura, totales } = mentorData;

  // Sin contenido cargado todavía → mensaje "próximamente" en lugar de una vista vacía.
  const sinContenido = !(estructura?.modulos?.length) && !(mentorData.videos?.length);
  if (sinContenido) {
    return proximamenteScreen;
  }

  // Capas de fin de lección (avanzar / felicitación): las usan el video y el
  // contenido interactivo.
  const capasCierre = (
    <>
      {/* Capa superpuesta al completar la lección (primera vez): invita a avanzar */}
      {showAdvanceOverlay && (
        <AdvanceOverlay>
          <AdvanceCard onClick={e => e.stopPropagation()}>
            <AdvanceClose onClick={() => { detenerVigiaCompartir(); setCompartiendo(false); setShowAdvanceOverlay(false); }} title="Cerrar">×</AdvanceClose>
            <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
            <AdvanceTitle>¡Lección completada!</AdvanceTitle>
            <AdvanceSub>Ya puedes continuar con la siguiente lección.</AdvanceSub>
            <AdvanceGoBtn onClick={() => { detenerVigiaCompartir(); setCompartiendo(false); handleAdvance(); }}>Avanzar a la siguiente lección →</AdvanceGoBtn>
            {compartirHabilitado && (compartiendo ? (
              <AdvanceShareNote role="status">{t('mentor_compartir_esperando')}</AdvanceShareNote>
            ) : (
              <AdvanceShareBtn type="button" onClick={() => compartirEnLinkedIn(false, avanzarTrasCompartir)}>
                <LinkedInIcon />
                {t('mentor_compartir_linkedin')}
              </AdvanceShareBtn>
            ))}
          </AdvanceCard>
        </AdvanceOverlay>
      )}
  
      {/* Capa de felicitación al completar la ÚLTIMA lección (primera vez) */}
      {showCongratsOverlay && (
        <AdvanceOverlay>
          <AdvanceCard onClick={e => e.stopPropagation()}>
            <AdvanceClose onClick={() => setShowCongratsOverlay(false)} title="Cerrar">×</AdvanceClose>
            <div style={{ fontSize: 44, marginBottom: 8 }}>🎓</div>
            <AdvanceTitle>{t('mentor_felicidades_titulo')}</AdvanceTitle>
            <AdvanceSub>
              {t('mentor_felicidades_msg')}
              {examenEnPrograma && <> {t('mentor_examen_disponible')}</>}
            </AdvanceSub>
            {examenEnPrograma && (
              <AdvanceGoBtn onClick={irAlExamen} style={{ marginBottom: 10 }}>
                📝 {t('mentor_ir_examen')}
              </AdvanceGoBtn>
            )}
            <AdvanceGoBtn onClick={finalizarPrograma}>{t('mentor_volver_contenidos')}</AdvanceGoBtn>
            {compartirHabilitado && (
              <>
                <AdvanceShareBtn type="button" onClick={() => compartirEnLinkedIn(true)}>
                  <LinkedInIcon />
                  {t('mentor_compartir_linkedin')}
                </AdvanceShareBtn>
                {textoCopiado && <AdvanceShareNote role="status">{t('mentor_compartir_copiado')}</AdvanceShareNote>}
              </>
            )}
          </AdvanceCard>
        </AdvanceOverlay>
      )}
    </>
  );

  return (
    <PageWrapper
      $light={lightMode}
      $collapsed={sidebarCollapsed}
      $right={sidebarRight}
      $tutor={tutorAbierto && (chatHabilitado || quizActive)}
    >
      {user?.modo_pruebas && pruebasOculto ? (
        <PruebasMostrar type="button" onClick={() => alternarPruebas(false)} title="Mostrar el panel de modo pruebas">
          Modo pruebas ▴
        </PruebasMostrar>
      ) : null}
      {user?.modo_pruebas && !pruebasOculto ? (
        <PruebasPanel>
          <PruebasTitulo>
            Modo pruebas
            <button type="button" onClick={() => alternarPruebas(true)} title="Ocultar el panel" aria-label="Ocultar el panel de modo pruebas">✕</button>
          </PruebasTitulo>
          <PruebasFila>
            <PruebasBtn onClick={() => completarPrueba('leccion')}>✓ Lección</PruebasBtn>
            <PruebasBtn onClick={() => completarPrueba('modulo')}>✓ Módulo</PruebasBtn>
            <PruebasBtn onClick={() => completarPrueba('documento')}>✓ Curso</PruebasBtn>
            <PruebasBtn onClick={() => completarPrueba('evaluacion')}>✓ Evaluación</PruebasBtn>
          </PruebasFila>
          {pruebasMsg && <PruebasMsg>{pruebasMsg}</PruebasMsg>}
        </PruebasPanel>
      ) : null}

      {/* Ancla silenciosa de Media Session: controla la sesión de medios del SO
          (pantalla de bloqueo / teclas multimedia) apuntando a NUESTRA página. */}
      <audio ref={silentAudioRef} src={SILENT_WAV} loop preload="auto"
             style={{ display: 'none' }} aria-hidden="true" />

      {/* Sidebar overlay for mobile */}
      <SidebarOverlay $open={sidebarOpen} onClick={() => setSidebarOpen(false)} />

      {/* ─── SIDEBAR ─── */}
      <Sidebar $open={sidebarOpen} $collapsed={sidebarCollapsed} $right={sidebarRight}>
        <SidebarContent $collapsed={sidebarCollapsed}>
          <SidebarHeader>
            <SidebarHeaderRow>
              <BackButton onClick={() => navigate(volverA.current)}>
                &larr; Contenidos
              </BackButton>
              <SidebarAction
                onClick={toggleSidebarPosition}
                title={sidebarRight ? 'Mover sidebar a la izquierda' : 'Mover sidebar a la derecha'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {sidebarRight ? (
                    <>
                      <polyline points="15 18 9 12 15 6" />
                      <polyline points="21 18 15 12 21 6" />
                      <line x1="3" y1="6" x2="3" y2="18" />
                    </>
                  ) : (
                    <>
                      <polyline points="9 18 15 12 9 6" />
                      <polyline points="3 18 9 12 3 6" />
                      <line x1="21" y1="6" x2="21" y2="18" />
                    </>
                  )}
                </svg>
              </SidebarAction>
            </SidebarHeaderRow>
            <ProgramTitle>{tlTitulo(estructura, 'titulo_programa') || mentorData.document_title}</ProgramTitle>
            <ProgressBar>
              <ProgressFill $percent={totales?.porcentaje || 0} />
            </ProgressBar>
            <ProgressText>
              {totales?.lecciones_completadas || 0} / {totales?.total_lecciones || 0} lecciones ({totales?.porcentaje || 0}%)
            </ProgressText>
          </SidebarHeader>

          <ModuleList>
            {estructura?.modulos?.map((mod, mi) => {
              const modNum = mod.numero || mi + 1;
              const modStatus = getModuleStatus(modNum);

              return (
                <ModuleGroup key={modNum}>
                  <ModuleHeader
                    $status={modStatus}
                    $mayus={proyecto?.config_json?.mentor_modulos_mayusculas !== false}
                    onClick={() => toggleModule(modNum)}
                  >
                    {modStatus === 'completed' ? '\u2713' : modStatus === 'current' ? '\u25B6' : '\u25CB'}{' '}
                    {tlTitulo(mod)}
                  </ModuleHeader>

                  <LessonList $expanded={expandedModules[modNum]}>
                    {mod.lecciones?.map((lec, li) => {
                      const lecNum = lec.numero || li + 1;
                      const status = getLessonStatus(modNum, lecNum);
                      // "Activa" = la que suena en el reproductor (en repaso no es
                      // la del avance real), que es donde va el tiempo en vivo.
                      const isActive = modNum === moduloEnPantalla && lecNum === leccionEnPantalla;

                      // Docente asignado: prioriza la lección, si no el módulo
                      const doc = (lec.docente_foto || lec.docente_nombre)
                        ? lec
                        : ((mod.docente_foto || mod.docente_nombre) ? mod : null);
                      const docFoto = doc ? resolveDocenteFoto(doc.docente_foto) : null;
                      const docFlag = doc ? flagFromPais(doc.docente_pais) : null;

                      // Progreso del video de la lección.
                      // En la lección ACTIVA usamos el tiempo EN VIVO del reproductor;
                      // en las demás, el timestamp guardado (solo si no está completada).
                      const vid = mentorData.videos?.find(
                        v => v.modulo_numero === modNum && v.leccion_numero === lecNum
                      );
                      // Solo con Vimeo: en contenido embebido el tiempo del reproductor
                      // es el de la lección de video anterior.
                      const liveOn = isActive && videoDuration > 0 && !!videoActual?.vimeo_id;
                      const ts = liveOn ? currentTime : (vid?.timestamp_actual || 0);
                      const dur = liveOn ? videoDuration : (vid?.duracion_segundos || 0);
                      const yaCompletado = vid?.completado === 1;
                      const enProgreso = dur > 0 && ts > 2 && (liveOn || !yaCompletado);
                      const pct = enProgreso ? (ts / dur) * 100 : 0;

                      return (
                        <React.Fragment key={lecNum}>
                        <LessonItem
                          $active={isActive}
                          $completed={status === 'completed'}
                          $locked={status === 'locked'}
                          onClick={() => {
                            if (status !== 'locked') {
                              loadLesson(modNum, lecNum);
                            }
                          }}
                        >
                          <StatusDot $completed={status === 'completed'} $active={isActive} />
                          {doc && (
                            <DocenteAvatar title={doc.docente_nombre || ''}>
                              {docFoto
                                ? <DocenteFoto src={docFoto} alt={doc.docente_nombre || ''} loading="lazy" />
                                : <DocenteFotoFallback>{(doc.docente_nombre || '?').charAt(0).toUpperCase()}</DocenteFotoFallback>}
                              {docFlag && <DocenteBandera src={docFlag} alt={doc.docente_pais || ''} loading="lazy" />}
                            </DocenteAvatar>
                          )}
                          <LessonBody>
                            <LessonTitleText>{tlTitulo(lec)}</LessonTitleText>
                            {enProgreso && (
                              <LessonProgress>
                                <MiniBar><MiniBarFill style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} /></MiniBar>
                                <MiniTime>{formatTime(ts)} / {formatTime(dur)}</MiniTime>
                              </LessonProgress>
                            )}
                          </LessonBody>
                        </LessonItem>
                        {examenes.filter(ex => ex.alcance === 'leccion' && ex.leccion_id === lec.leccion_id).map(filaExamen)}
                        </React.Fragment>
                      );
                    })}
                    {examenes.filter(ex => ex.alcance === 'modulo' && ex.modulo_id === mod.modulo_id).map(filaExamen)}
                  </LessonList>
                </ModuleGroup>
              );
            })}
            {examenCurso && filaExamen(examenCurso)}
          </ModuleList>
        </SidebarContent>

        <CollapseTab
          $collapsed={sidebarCollapsed}
          $right={sidebarRight}
          onClick={toggleSidebarCollapse}
          title={sidebarCollapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </CollapseTab>
      </Sidebar>

      {/* ─── MAIN AREA ─── */}
      <MainArea $right={sidebarRight}>
        {mentorData.estado === 'completado' && (
          <CompletedBanner>
            <h2>{t('mentor_programa_completado')}</h2>
            <p>{t('mentor_programa_completado_msg')}</p>
          </CompletedBanner>
        )}

        {/* Video Header */}
        <VideoSection>
          <VideoHeader>
            <HamburgerBtn onClick={() => setSidebarOpen(true)}>
              &#9776;
            </HamburgerBtn>
            <ThemeToggle $light={lightMode} onClick={toggleTheme} title={lightMode ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}>
              <ThemeIcon $side="left" $visible={lightMode}>&#9728;</ThemeIcon>
              <ThemeIcon $side="right" $visible={!lightMode}>&#9789;</ThemeIcon>
            </ThemeToggle>
            {mentorData?.document_logo && (
              <img
                src={`/backend/${mentorData.document_logo}`}
                alt=""
                style={{ height: '40px', objectFit: 'contain', flexShrink: 0 }}
              />
            )}
            <VideoTitle>
              {videoActual?.titulo_completo || 'Cargando...'}
            </VideoTitle>
          </VideoHeader>

          {/* Video Player */}
          {videoActual?.vimeo_id ? (
            <VideoContainer onMouseMove={handleMouseMoveOnVideo}>
              <iframe
                key={playerKey}
                id={IFRAME_ID}
                src={`https://player.vimeo.com/video/${videoActual.vimeo_id}?h=${videoActual.hash_privacidad || ''}&autoplay=0&autopause=0&controls=0&muted=0&dnt=1&playsinline=1&transparent=0&responsive=1&keyboard=0&portrait=0&title=0&byline=0${autoRetryRef.current > 0 ? `&_r=${playerKey}` : ''}`}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={videoActual.titulo_completo}
              />

              {/* Click area for play/pause */}
              {isPlayerReady && !showStartOverlay && (
                <ControlsClickArea onClick={handleVideoAreaClick} />
              )}

              {/* Big play button when paused */}
              {isPlayerReady && videoStatus !== 'playing' && videoStatus !== 'loading' && !showStartOverlay && (
                <BigPlayBtn onClick={togglePlayPause}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </BigPlayBtn>
              )}

              {/* Subtítulo activo (overlay propio, sobre la control bar) */}
              {isPlayerReady && activeCue && (
                <SubtitleOverlay>{activeCue.text}</SubtitleOverlay>
              )}

              {/* Custom control bar */}
              {isPlayerReady && (
                <ControlsOverlay>
                  <ControlBar $visible={showControls || videoStatus !== 'playing'}>
                    <CtrlBtn onClick={togglePlayPause} title={videoStatus === 'playing' ? 'Pausar' : 'Reproducir'}>
                      {videoStatus === 'playing' ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                      )}
                    </CtrlBtn>
                    <CtrlBtn onClick={handleRewind10} title="Retroceder 10s">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6h-2c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/><text x="10" y="16" fontSize="7" textAnchor="middle" fill="currentColor">10</text></svg>
                    </CtrlBtn>
                    <TimeLabel>{formatTime(currentTime)}</TimeLabel>
                    <ProgressBarWrapper onClick={handleProgressClick}>
                      <MaxReachedBar $percent={Math.min(maxReachedPercent, 100)} />
                      <ProgressBarFill $percent={Math.min(progressPercent, 100)} />
                    </ProgressBarWrapper>
                    <TimeLabel>{formatTime(videoDuration)}</TimeLabel>

                    {/* Silenciar / activar audio */}
                    <CtrlBtn onClick={toggleMute} title={isMuted ? 'Activar sonido' : 'Silenciar'}>
                      {isMuted ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
                      )}
                    </CtrlBtn>

                    {/* Mini-reproductor flotante nativo: flota el VIDEO sobre otras pestañas */}
                    {pipSupported && (
                      <CtrlBtnActive $active={pipActive} onClick={togglePiP} title={pipActive ? 'Salir del mini-reproductor' : 'Mini-reproductor (flota el video)'}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 11h-8v6h8v-6zm4 8V4.98C23 3.88 22.1 3 21 3H3c-1.1 0-2 .88-2 1.98V19c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2zm-2 .02H3V4.97h18v14.05z"/></svg>
                      </CtrlBtnActive>
                    )}

                    {/* Mini-control flotante (Document PiP): 1 clic y flota sobre otras
                        pestañas con lecciones + controles + temporizador (el audio sigue) */}
                    {docPipSupported && (
                      <CtrlBtnActive
                        $active={miniOpen}
                        onClick={openMiniControl}
                        title={miniOpen ? 'Cerrar mini-control' : 'Mini-control flotante (lecciones + controles)'}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zM5 7h6v2H5V7zm0 4h6v2H5v-2zm0 4h6v2H5v-2zm8-8h6v10h-6V7z"/></svg>
                      </CtrlBtnActive>
                    )}

                    {/* Subtítulos: botón CC (+ selector de idioma si hay varios) */}
                    {idiomasDisponibles.length > 0 && (
                      <div style={{ position: 'relative' }}>
                        <CtrlBtnActive
                          $active={subtitlesOn}
                          onClick={() => idiomasDisponibles.length > 1 ? setShowSubMenu(v => !v) : toggleSubtitles()}
                          title={subtitlesOn ? 'Subtítulos activados' : 'Subtítulos desactivados'}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zM5 11h3v2H5v-2zm10 6H5v-2h10v2zm4 0h-2v-2h2v2zm0-4H10v-2h9v2z"/>
                          </svg>
                        </CtrlBtnActive>
                        {showSubMenu && (
                          <SubtitleMenu>
                            <SubtitleMenuItem $active={!subtitlesOn} onClick={toggleSubtitles}>
                              {t('mentor_subs_desactivados')} {!subtitlesOn && '✓'}
                            </SubtitleMenuItem>
                            {idiomasDisponibles.map(lang => (
                              <SubtitleMenuItem
                                key={lang}
                                $active={subtitlesOn && idiomaEfectivo === lang}
                                onClick={() => selectSubtitleLang(lang)}
                              >
                                {NOMBRE_IDIOMA[lang] || lang.toUpperCase()}
                                {subtitlesOn && idiomaEfectivo === lang && ' ✓'}
                              </SubtitleMenuItem>
                            ))}
                          </SubtitleMenu>
                        )}
                      </div>
                    )}
                  </ControlBar>
                </ControlsOverlay>
              )}

              {!isPlayerReady && !loadError && <VideoLoader>{t('mentor_cargando_video')}</VideoLoader>}
              {loadError && (
                <VideoErrorBox>
                  <svg className="err-icon" width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
                  </svg>
                  <div className="err-title">{t('mentor_video_error_titulo')}</div>
                  <div className="err-msg">{t('mentor_video_error_msg')}</div>
                  <RetryBtn onClick={handleRetryLoad}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 7.74 10h-2.08A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                    </svg>
                    {t('mentor_reintentar')}
                  </RetryBtn>
                </VideoErrorBox>
              )}
              {showSeekWarning && <SeekWarning>{t('mentor_no_adelantar')}</SeekWarning>}

              {showStartOverlay && (
                <UnmuteBanner onClick={handleManualStart}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.17v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                  </svg>
                  {t('mentor_activar_audio')}
                </UnmuteBanner>
              )}

              {capasCierre}
            </VideoContainer>
          ) : esEmbebido(videoActual) ? (
            // Contenido embebido: paquete interactivo (Articulate/SCORM), video de
            // YouTube o PDF. Se abre en un iframe y el alumno lo da por visto con el botón.
            <>
              <VideoContainer $embebido={videoActual.tipo}>
                {videoActual.tipo === 'lectura' ? (
                  <VisorLectura key={`lec-${videoActual.id}`} titulo={videoActual.titulo_completo} texto={videoActual.texto_lectura} />
                ) : videoActual.tipo === 'pdf' ? (
                  <VisorPdf key={`pdf-${videoActual.id}`} url={`/backend${videoActual.file_path}`} titulo={videoActual.titulo_completo} />
                ) : (
                  videoActual.tipo === 'interactivo' && !/^https?:\/\//.test(videoActual.file_path) ? (
                    <IframeScorm
                      key={`int-${videoActual.id}`}
                      media={videoActual}
                      documentId={documentId}
                      user={user}
                      fallback={`/backend${videoActual.file_path}`}
                      title={videoActual.titulo_completo}
                      onAvance={setAvanceInteractivo}
                      onCompletado={() => { if (!videoActual.completado && !quizCompleto && !quizActive) completarInteractivoAuto(); }}
                    />
                  ) : (
                    <IframeConCarga
                      key={`int-${videoActual.id}`}
                      src={/^https?:\/\//.test(videoActual.file_path) ? videoActual.file_path : `/backend${videoActual.file_path}`}
                      title={videoActual.titulo_completo}
                    />
                  )
                )}
                {capasCierre}
              </VideoContainer>
              <InteractivoBarra>
                <span style={{ display: 'flex', flexDirection: 'column' }}>{videoActual.completado || quizCompleto
                  ? 'Lección completada.'
                  : entregaPendiente
                    ? (entregaEstados[leccionEnPantallaObj.leccion_id] === 'pendiente'
                      ? 'Tu entrega está en revisión: la lección se completa cuando el tutor la apruebe.'
                      : 'Revisa el contenido y sube tu entrega abajo para completar esta lección.')
                  : videoActual.tipo === 'pdf'
                    ? 'Lee el documento y márcalo como completado para avanzar.'
                    : videoActual.tipo === 'lectura'
                    ? 'Lee el texto y márcalo como completado para avanzar.'
                    : videoActual.tipo === 'video'
                      ? 'Mira el video y márcalo como completado para avanzar.'
                      : avanceInteractivo?.diapositivas_total > 0
                        ? 'Recorre todas las diapositivas: la lección se completa sola al terminar.'
                        : 'Recorre el contenido interactivo y márcalo como completado para avanzar.'}
                  {videoActual.tipo === 'interactivo' && avanceInteractivo?.nota != null && (
                    <NotaInteractivo $ok={avanceInteractivo.resultado !== 'reprobado'}>
                      Nota del contenido: <b>{avanceInteractivo.nota} %</b>
                      {avanceInteractivo.resultado && <em>{avanceInteractivo.resultado === 'aprobado' ? 'Aprobado' : 'No aprobado: vuelve a intentarlo para completar la lección'}</em>}
                    </NotaInteractivo>
                  )}
                  {videoActual.tipo === 'interactivo' && avanceInteractivo?.diapositivas_total > 0 && (() => {
                    const a = avanceInteractivo; const pct = Math.round((a.diapositivas_vistas / a.diapositivas_total) * 100);
                    const min = Math.round((Number(a.tiempo_visible_seg || 0)) / 60);
                    return (
                      <AvanceInteractivo>
                        <i role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Avance del contenido"><b style={{ width: `${pct}%` }} /></i>
                        <span>{a.diapositivas_vistas} de {a.diapositivas_total} diapositivas{a.diapositiva_actual ? ` · ${a.diapositiva_actual}` : ''}{min > 0 ? ` · ${min} min` : ''}</span>
                      </AvanceInteractivo>
                    );
                  })()}
                </span>
                <span style={{ display: 'inline-flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {!(videoActual.completado || quizCompleto) && !entregaPendiente && (
                    <InteractivoBoton type="button" onClick={completarInteractivo}
                      style={videoActual.tipo === 'interactivo' && avanceInteractivo?.diapositivas_total > 0 ? { background: 'transparent', color: '#94a3b8', boxShadow: 'inset 0 0 0 1px rgba(148,163,184,.4)' } : undefined}>
                      Marcar como completada
                    </InteractivoBoton>
                  )}
                </span>
              </InteractivoBarra>
            </>
          ) : (
            <VideoContainer>
              <VideoLoader>{t('mentor_no_video')}</VideoLoader>
            </VideoContainer>
          )}
        </VideoSection>

        {/* Indicaciones de la lección (texto que escribe el admin) */}
        {(() => {
          const lec = mentorData?.estructura?.modulos?.[(moduloEnPantalla || 1) - 1]?.lecciones?.[(leccionEnPantalla || 1) - 1];
          const texto = (lec?.descripcion || '').trim();
          if (!texto) return null;
          return (
            <Indicaciones>
              <strong>Indicaciones</strong>
              {texto.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}
            </Indicaciones>
          );
        })()}

        {/* Entrega de archivos que pide la lección (se configura en el admin con 📎) */}
        {leccionEnPantallaObj?.entrega && (
          <MiEntrega
            leccionId={leccionEnPantallaObj.leccion_id}
            entrega={leccionEnPantallaObj.entrega}
            onEstado={(estado, completada) => {
              setEntregaEstados(prev => ({ ...prev, [leccionEnPantallaObj.leccion_id]: estado }));
              // Automática: el servidor ya dio la lección por completada; se refleja sin recargar.
              if (completada && videoActual?.id) {
                setMentorData(prev => {
                  if (!prev) return prev;
                  const yaEstaba = (prev.videos || []).some(v => v.video_id === videoActual.id && v.completado);
                  const t = prev.totales || {};
                  const hechas = (t.lecciones_completadas || 0) + (yaEstaba ? 0 : 1);
                  return {
                    ...prev,
                    video_actual: prev.video_actual && prev.video_actual.id === videoActual.id ? { ...prev.video_actual, completado: 1 } : prev.video_actual,
                    videos: (prev.videos || []).map(v => (v.video_id === videoActual.id ? { ...v, completado: 1 } : v)),
                    // El contador de la barra lateral también, sin esperar a recargar.
                    totales: { ...t, lecciones_completadas: hechas, porcentaje: t.total_lecciones ? Math.round((hechas / t.total_lecciones) * 100) : t.porcentaje },
                  };
                });
                isReplay.current = false;
                setQuizCompleto(true);
              }
            }}
          />
        )}

        {/* Materiales de apoyo (Fase 15.6 — contenido mixto) */}
        {(() => {
          const est = mentorData?.estructura;
          if (!est?.modulos) return null;
          const mod = est.modulos[(moduloEnPantalla || 1) - 1];
          const lec = mod?.lecciones?.[(leccionEnPantalla || 1) - 1];
          // Todo lo que no es el contenido principal de la lección (ese va en el reproductor).
          const materiales = (lec?.items || []).filter(it => it.media_id !== videoActual?.id && it.tipo !== 'video' && it.tipo !== 'interactivo');
          if (materiales.length === 0) return null;
          return (
            <div style={{ padding: '0.75rem 1rem', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569', marginBottom: '0.5rem' }}>
                Material de apoyo
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {materiales.map((item, idx) => (
                  <a
                    key={idx}
                    href={item.file_path ? `/backend${item.file_path}` : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                      padding: '0.4rem 0.75rem', background: '#fff', borderRadius: '8px',
                      border: '1px solid #e2e8f0', fontSize: '0.82rem', color: '#334155',
                      textDecoration: 'none', transition: 'all 0.15s',
                    }}
                    onMouseOver={e => { e.currentTarget.style.borderColor = '#14b6cb'; e.currentTarget.style.color = '#0e7490'; }}
                    onMouseOut={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#334155'; }}
                  >
                    <span>{item.tipo === 'pdf' ? '📑' : item.tipo === 'imagen' ? '🖼️' : item.tipo === 'audio' ? '🎵' : '📄'}</span>
                    <span>{item.titulo || item.original_name || 'Archivo'}</span>
                  </a>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Navigation */}
        <NavBar>
          <NavBtn
            onClick={handlePrevious}
            disabled={!canGoPrevious()}
          >
            &larr; Anterior
          </NavBtn>

          <NavStatus>
            {mentorData.estado === 'completado'
              ? 'Programa completado'
              : `Módulo ${moduloEnPantalla} - Lección ${leccionEnPantalla}`
            }
          </NavStatus>

          {/* Examen del programa: visible solo si el contenido lo trae dentro del
              modo mentor; se habilita al completar todas las lecciones. */}
          {examenEnPrograma && (
            <NavBtn
              onClick={irAlExamen}
              disabled={!examenHabilitado}
              $primary={examenHabilitado}
              $glow={examenHabilitado}
              title={examenHabilitado ? t('mentor_ir_examen') : t('mentor_examen_bloqueado')}
            >
              📝 {t('mentor_ir_examen')}
            </NavBtn>
          )}

          <NavBtn
            $primary
            $glow={avanzarListo}
            onClick={handleAdvance}
            disabled={!canAdvance()}
          >
            Siguiente &rarr;
          </NavBtn>
        </NavBar>

        {(chatHabilitado || quizActive) && !tutorAbierto && (
          <TutorLlamador
            docente={docenteTutor}
            aviso={avisoTutor}
            destacar={tutorDestacado}
            alto={esEmbebido(videoActual) && !videoActual.vimeo_id}
            onAbrir={() => { abrirTutor(true); marcarTutorConocido(); }}
            t={t}
          />
        )}
      </MainArea>

      {/* El tutor: columna propia junto al video (antes era una barra de 44px
          al fondo que nadie veía). Si el proyecto apagó el chat, solo aparece
          durante el quiz, que se responde por este mismo campo. */}
      {(chatHabilitado || quizActive) && (
        <TutorPanel
          abierto={tutorAbierto}
          onAbrir={() => { abrirTutor(true); marcarTutorConocido(); }}
          onCerrar={() => abrirTutor(false)}
          indiceALaDerecha={sidebarRight}
          docente={docenteTutor}
          userName={mentorData.user_name}
          mensajes={chatMessages}
          cargando={chatLoading}
          parcial={realtimePartial}
          error={realtimeError === 'REALTIME_NOT_AVAILABLE' ? t('tutor_voz_no_disponible') : realtimeError}
          micNoDisponible={realtimeMicUnavailable}
          input={chatInput}
          onInput={setChatInput}
          onSubmit={handleChatSubmit}
          quizActive={quizActive}
          quizPregunta={quizPregunta}
          sugerencias={sugerenciasVisibles}
          onSugerencia={enviarSugerencia}
          voz={{
            soportado: isRealtimeSupported(),
            activo: realtimeActive,
            etiqueta: getRealtimeLabel(),
            hablando: realtimeState === SESSION_STATES.AI_SPEAKING,
            onToggle: toggleRealtime,
            onInterrumpir: () => realtimeSessionRef.current?.interrupt(),
          }}
          aviso={avisoTutor}
          onAviso={enviarSugerencia}
          onDescartarAviso={() => setAvisoTutor(null)}
          destacar={tutorDestacado}
          t={t}
        />
      )}
    </PageWrapper>
  );
};

export default MentorPage;
